import React, { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";
import { dispararActualizacionEnSegundoPlano } from "./servicios/actualizarPresupuestosTotales";
import { obtenerPermisosCliente, puedeAccederAVista } from "./permisos";
import { BarChart3, TrendingUp, TrendingDown, Wallet, Building2, Users, Plus, Edit, Trash2, Save, X, Eye, Search, Filter, Download, Upload, Calendar, DollarSign, FileText, User, Clock, MapPin, Loader2, CheckCircle2, XCircle, AlertCircle, Info, ChevronDown, Settings, RefreshCw } from "lucide-react";
import { calcularConSistemaActualizacionPorcentajeAdicionales as calcularConSistemaActualizacionPorcentajeAdicionalesShared, convertirMonto as convertirMontoShared, formatearFechaSinTimezone as formatearFechaSinTimezoneShared, formatearFechaParaIndice as formatearFechaParaIndiceShared } from "./utils/calculosHonorarios";

// Configuración anti-zoom
if (typeof document !== "undefined") {
  let metaViewport = document.querySelector('meta[name="viewport"]');
  if (!metaViewport) {
    metaViewport = document.createElement("meta");
    metaViewport.name = "viewport";
    document.head.appendChild(metaViewport);
  }
  metaViewport.content =
    "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover";
}

// Colores por vista
const VISTA_COLORS = {
  "honorarios-mo": {
    gradient: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
    solid: "#8b5cf6",
    light: "#8b5cf610",
    description: "Control de pagos",
  },
};

const getVistaColor = (vistaId) => {
  return VISTA_COLORS[vistaId] || VISTA_COLORS["honorarios-mo"];
};

// CustomDropdown unificado
const CustomDropdown = ({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  icon: Icon,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const selected = options.find((opt) => opt.value === value);
    setSelectedOption(selected);
  }, [value, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} style={{ position: "relative", width: "100%" }}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{
          width: "100%",
          padding: "14px 16px",
          border: "2px solid #f1f5f9",
          borderRadius: "12px",
          background: disabled ? "#f8fafc" : "white",
          color: disabled ? "#94a3b8" : "#1e293b",
          fontSize: "16px",
          fontWeight: "500",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: disabled ? "not-allowed" : "pointer",
          outline: "none",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          minHeight: "44px",
          touchAction: "manipulation",
          boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
        }}
        onFocus={(e) => {
          if (!disabled) {
            e.target.style.borderColor = "#8b5cf6";
            e.target.style.boxShadow = "0 0 0 3px rgba(139, 92, 246, 0.1)";
          }
        }}
        onBlur={(e) => {
          if (!disabled) {
            e.target.style.borderColor = "#f1f5f9";
            e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
          }
        }}
      >
        <div
          style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}
        >
          {Icon && <Icon size={16} color="#64748b" />}
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        </div>
        <ChevronDown
          size={16}
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            flexShrink: 0,
            color: "#64748b",
          }}
        />
      </button>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1000,
            background: "white",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
            marginTop: "4px",
            maxHeight: "200px",
            overflowY: "auto",
          }}
        >
          {options.map((option, index) => (
            <button
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "none",
                background: option.value === value ? "#eff6ff" : "transparent",
                color: option.value === value ? "#3b82f6" : "#374151",
                fontSize: "16px",
                fontWeight: option.value === value ? "600" : "400",
                textAlign: "left",
                cursor: "pointer",
                transition: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
                touchAction: "manipulation",
              }}
              onMouseEnter={(e) => {
                if (option.value !== value) {
                  e.target.style.backgroundColor = "#f8fafc";
                }
              }}
              onMouseLeave={(e) => {
                if (option.value !== value) {
                  e.target.style.backgroundColor = "transparent";
                }
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Modal personalizado para alertas/confirmaciones
function CustomModal({
  isOpen,
  onClose,
  type = "info",
  title,
  message,
  onConfirm,
  confirmText = "Aceptar",
  cancelText = "Cancelar",
  isMobile,
}) {
  if (!isOpen) return null;

  const getColors = () => {
    switch (type) {
      case "confirm":
        return {
          bg: "#fef3cd",
          border: "#f59e0b",
          text: "#92400e",
          button: "#f59e0b",
        };
      case "error":
        return {
          bg: "#fef2f2",
          border: "#ef4444",
          text: "#dc2626",
          button: "#ef4444",
        };
      case "success":
        return {
          bg: "#f0fdf4",
          border: "#22c55e",
          text: "#166534",
          button: "#22c55e",
        };
      default:
        return {
          bg: "#f0f9ff",
          border: "#3b82f6",
          text: "#1d4ed8",
          button: "#3b82f6",
        };
    }
  };

  const colors = getColors();

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10001, // Mayor que posibles modales padres
        padding: "20px",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "16px",
          padding: "24px",
          width: "100%",
          maxWidth: isMobile ? "90%" : "400px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          border: `2px solid ${colors.border}`,
          animation: "scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div
          style={{
            textAlign: "center",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              width: "64px",
              height: "64px",
              margin: "0 auto 16px",
              borderRadius: "32px",
              background: colors.bg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {type === "confirm" && (
              <AlertCircle size={32} color={colors.text} />
            )}
            {type === "error" && <XCircle size={32} color={colors.text} />}
            {type === "success" && (
              <CheckCircle2 size={32} color={colors.text} />
            )}
            {type === "info" && <Info size={32} color={colors.text} />}
          </div>
          <h3
            style={{
              margin: "0 0 8px 0",
              fontSize: "18px",
              fontWeight: "600",
              color: colors.text,
            }}
          >
            {title}
          </h3>
          <p
            style={{
              margin: 0,
              fontSize: "14px",
              color: "#64748b",
              lineHeight: "1.4",
            }}
          >
            {message}
          </p>
        </div>

        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: type === "confirm" ? "space-between" : "center",
          }}
        >
          {type === "confirm" && (
            <button
              onClick={onClose}
              style={{
                flex: 1,
                padding: "12px 20px",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                background: "white",
                color: "#64748b",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
              className="button-hover"
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={() => {
              if (type === "confirm" && onConfirm) {
                onConfirm();
              }
              onClose();
            }}
            style={{
              flex: type === "confirm" ? 1 : "auto",
              padding: "12px 20px",
              border: "none",
              borderRadius: "8px",
              background: colors.button,
              color: "white",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              minWidth: type !== "confirm" ? "120px" : "auto",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
            className="button-hover"
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

function HonorariosMO({ constructora, usuario }) {
  const [proyectos, setProyectos] = useState([]);
  const [proyectoSeleccionado, setProyectoSeleccionado] = useState(() => {
    // Intentar restaurar el último proyecto seleccionado desde localStorage
    try {
      const saved = localStorage.getItem('honorariosMO_proyectoSeleccionado');
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.error('Error restaurando proyecto seleccionado:', error);
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Estados para permisos de cliente
  const [datosCliente, setDatosCliente] = useState(null);
  const [verificandoPermisos, setVerificandoPermisos] = useState(true);

  // Estados para los datos calculados
  const [datosHonorarios, setDatosHonorarios] = useState(null);
  const [datosManoObra, setDatosManoObra] = useState(null);
  const [datosLlaveEnMano, setDatosLlaveEnMano] = useState(null);
  const [datosAdicionales, setDatosAdicionales] = useState(null);
  const [cotizacionDolar, setCotizacionDolar] = useState(null);
  const [indicesCAC, setIndicesCAC] = useState([]);

  // Estados para alertas personalizadas
  const [customModal, setCustomModal] = useState({
    isOpen: false,
    type: "info",
    title: "",
    message: "",
    onConfirm: null,
  });

  // Función para guardar el proyecto seleccionado en localStorage
  const guardarProyectoSeleccionado = (proyecto) => {
    try {
      localStorage.setItem('honorariosMO_proyectoSeleccionado', JSON.stringify(proyecto));
    } catch (error) {
      console.error('Error guardando proyecto seleccionado:', error);
    }
  };

  const showCustomAlert = (type, title, message, onConfirm = null) => {
    setCustomModal({
      isOpen: true,
      type,
      title,
      message,
      onConfirm,
    });
  };

  // ESC handler obligatorio
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setCustomModal((prev) => ({ ...prev, isOpen: false }));
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  // Scroll reset al cargar
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Verificar permisos de cliente al inicio
  useEffect(() => {
    const verificarPermisos = async () => {
      if (!usuario || !constructora) {
        setVerificandoPermisos(false);
        return;
      }

      console.log("🔍 VERIFICANDO PERMISOS PARA USUARIO:", usuario);

      const permisos = await obtenerPermisosCliente(usuario, constructora);
      console.log("✅ PERMISOS OBTENIDOS:", permisos);

      setDatosCliente(permisos);

      // Verificar si puede acceder a esta vista
      if (
        permisos.esCliente &&
        !puedeAccederAVista(permisos.permisos, "honorarios-mo")
      ) {
        showCustomAlert(
          "error",
          "Acceso Denegado",
          "No tienes permisos para acceder a esta sección. Contacta a tu constructora si necesitas más información."
        );
        setVerificandoPermisos(false);
        return;
      }

      setVerificandoPermisos(false);
    };

    verificarPermisos();
  }, [usuario, constructora]);

  // FUNCIÓN: Formatear fecha sin conversión de timezone
  const formatearFechaSinTimezone = (fechaString) => {
    if (!fechaString) return new Date().toISOString().split("T")[0];
    if (fechaString.includes("T")) {
      return fechaString.split("T")[0];
    }
    return fechaString;
  };

  // FUNCIÓN: Detectar si es método personalizado (CORREGIDA)
  const esMetodoPersonalizado = (metodo) => {
    if (!metodo) return false;
    // Si NO es uno de los 3 oficiales, entonces ES personalizado
    return !["CAC_GENERAL", "CAC_MATERIALES", "CAC_MANO_OBRA"].includes(metodo);
  };

  // FUNCIÓN AUXILIAR: Convertir montos (MEJORADA)
  const convertirMonto = (
    monto,
    monedaOrigen,
    monedaDestino,
    tipoCambio = null
  ) => {
    const montoNum = parseFloat(monto || 0);

    if (monedaOrigen === monedaDestino) {
      return montoNum;
    }

    const tc = tipoCambio || cotizacionDolar || 1000;

    if (monedaOrigen === "USD" && monedaDestino === "ARS") {
      return montoNum * tc;
    } else if (monedaOrigen === "ARS" && monedaDestino === "USD") {
      return montoNum / tc;
    }

    return montoNum;
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (constructora?.id && !verificandoPermisos) {
      inicializarDatos();
    }
  }, [constructora, verificandoPermisos, datosCliente]);

  useEffect(() => {
    if (proyectoSeleccionado && indicesCAC.length > 0) {
      cargarDatosProyecto();
    }
  }, [proyectoSeleccionado, indicesCAC]);

  // ✅ NUEVO: Listener en tiempo real para detectar cambios en proyectos
  useEffect(() => {
    if (!proyectoSeleccionado?.id) return;

    console.log("🔄 Configurando listener para proyecto:", proyectoSeleccionado.id);

    const channel = supabase
      .channel(`proyecto-${proyectoSeleccionado.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'proyectos',
          filter: `id=eq.${proyectoSeleccionado.id}`
        },
        async (payload) => {
          console.log("🔔 PROYECTO ACTUALIZADO - Recargando datos:", payload.new);
          
          // Actualizar el proyecto seleccionado con los nuevos datos
          setProyectoSeleccionado(payload.new);
          guardarProyectoSeleccionado(payload.new);
          
          // Recargar los datos del proyecto
          await cargarDatosProyecto();
          
          showCustomAlert(
            "success",
            "Proyecto Actualizado",
            "Los datos del proyecto se han actualizado automáticamente"
          );
        }
      )
      .subscribe();

    return () => {
      console.log("🔕 Desconectando listener para proyecto:", proyectoSeleccionado.id);
      supabase.removeChannel(channel);
    };
  }, [proyectoSeleccionado?.id]);

  // FUNCIÓN DE INICIALIZACIÓN: Cargar datos en orden correcto
  const inicializarDatos = async () => {
    try {
      // 1. PRIMERO: Cargar proyectos y cotización en paralelo
      await Promise.all([cargarProyectos(), obtenerCotizacionDolar()]);

      // 2. SEGUNDO: Cargar índices CAC (CRÍTICO ANTES DE CALCULAR)
      await cargarIndicesCAC();

      console.log(
        "✅ INICIALIZACIÓN COMPLETA - Proyectos y índices CAC cargados"
      );
    } catch (err) {
      console.error("❌ ERROR EN INICIALIZACIÓN:", err);
      showCustomAlert(
        "error",
        "Error de Inicialización",
        "Error al cargar datos iniciales: " + err.message
      );
    }
  };

  // MODIFICAR: cargarProyectos para aplicar filtros de cliente

  // MODIFICAR: cargarProyectos para aplicar filtros de cliente
  const cargarProyectos = async () => {
    try {
      let query = supabase
        .from("proyectos")
        .select("*")
        .eq("constructora_id", constructora.id)
        .eq("activo", true)
        .is("deleted_at", null);

      // APLICAR FILTRO SI ES CLIENTE
      if (datosCliente?.esCliente && datosCliente?.proyectoId) {
        console.log(
          "🔒 APLICANDO FILTRO DE CLIENTE - Solo proyecto ID:",
          datosCliente.proyectoId
        );
        query = query.eq("id", datosCliente.proyectoId);
      } else {
        console.log("👤 USUARIO EMPLEADO - Cargando todos los proyectos");
        query = query
          .order("obra_finalizada", { ascending: true })
          .order("codigo");
      }

      const { data, error } = await query;

      if (error) {
        console.error("Error cargando proyectos:", error);
        showCustomAlert(
          "error",
          "Error",
          "Error al cargar proyectos: " + error.message
        );
      } else {
        console.log("📊 PROYECTOS CARGADOS:", data?.length || 0);
        setProyectos(data || []);

        if (data?.length > 0) {
          // SI ES CLIENTE: Seleccionar automáticamente su proyecto
          if (datosCliente?.esCliente) {
            const proyectoCliente = data.find(
              (p) => p.id === datosCliente.proyectoId
            );
            if (proyectoCliente) {
              console.log(
                "🎯 SELECCIONANDO PROYECTO DEL CLIENTE:",
                proyectoCliente.codigo
              );
              setProyectoSeleccionado(proyectoCliente);
              guardarProyectoSeleccionado(proyectoCliente);
            }
          } else {
            // Para empleados: verificar si el proyecto guardado sigue existiendo
            if (proyectoSeleccionado) {
              const proyectoExiste = data.find(p => p.id === proyectoSeleccionado.id);
              if (!proyectoExiste) {
                // El proyecto guardado ya no existe, establecer uno por defecto
                const primerActivo = data.find((p) => !p.obra_finalizada);
                const proyectoInicial = primerActivo || data[0];
                setProyectoSeleccionado(proyectoInicial);
                guardarProyectoSeleccionado(proyectoInicial);
              }
            } else {
              // No hay proyecto guardado, establecer uno por defecto
              const primerActivo = data.find((p) => !p.obra_finalizada);
              const proyectoInicial = primerActivo || data[0];
              setProyectoSeleccionado(proyectoInicial);
              guardarProyectoSeleccionado(proyectoInicial);
            }
          }
        }
      }
    } catch (err) {
      console.error("Error:", err);
      showCustomAlert("error", "Error", "Error: " + err.message);
    }
    setLoading(false);
  };

  const obtenerCotizacionDolar = async () => {
    try {
      const response = await fetch("https://dolarapi.com/v1/dolares/oficial");
      const data = await response.json();
      setCotizacionDolar(data.venta);
    } catch (err) {
      console.error("Error obteniendo cotización:", err);
      setCotizacionDolar(1000);
    }
  };

  const cargarIndicesCAC = async () => {
    try {
      const { data, error } = await supabase
        .from("indices_cac")
        .select("*")
        .order("fecha", { ascending: true });

      if (!error && data) {
        setIndicesCAC(data);
        console.log("🗄️ ÍNDICES CAC CARGADOS:", data.length, "registros");
      }
    } catch (err) {
      console.error("Error cargando índices CAC:", err);
    }
  };

  const cargarDatosProyecto = async () => {
    if (!proyectoSeleccionado) {
      console.log("❌ NO HAY PROYECTO SELECCIONADO");
      return;
    }

    if (indicesCAC.length === 0) {
      console.log("⚠️ ÍNDICES CAC NO CARGADOS AÚN - Esperando...");
      return;
    }

    console.log(
      `🔄 CARGANDO DATOS DEL PROYECTO: ${proyectoSeleccionado.codigo}`
    );
    console.log(`📊 ÍNDICES CAC DISPONIBLES: ${indicesCAC.length} registros`);

    setLoadingData(true);
    try {
      setDatosHonorarios(null);
      setDatosManoObra(null);
      setDatosLlaveEnMano(null);
      setDatosAdicionales(null);

      const proyecto = proyectoSeleccionado;

      if (proyecto.tipo_obra === "llave_en_mano") {
        await cargarDatosLlaveEnMano();
      } else {
        await cargarDatosHonorarios();
        if (proyecto.mano_obra_separada) {
          await cargarDatosManoObra();
        }
        // ✅ NUEVO: Cargar datos de adicionales si están habilitados
        if (proyecto.adicionales_honorarios_habilitado) {
          await cargarDatosAdicionales();
        } else {
          setDatosAdicionales(null);
        }
      }
    } catch (err) {
      console.error("Error cargando datos del proyecto:", err);
    }
    setLoadingData(false);
  };

  const cargarDatosLlaveEnMano = async () => {
    const proyecto = proyectoSeleccionado;

    if (!proyecto.monto_total_llave_en_mano) {
      setDatosLlaveEnMano({
        error: "No hay monto configurado para llave en mano",
      });
      return;
    }

    // Obtener todos los pagos del proyecto
    const { data: pagos, error: errorPagos } = await supabase
      .from("entradas")
      .select(
        "fecha, monto, moneda, tipo_cambio, concepto_id, comprobante_url, tiene_comprobante, conceptos_entrada!inner(nombre)"
      )
      .eq("proyecto_id", proyecto.id)
      .eq('activo', true)
      .is('deleted_at', null)
      .order("fecha", { ascending: true });

    if (errorPagos) {
      console.error("Error cargando pagos llave en mano:", errorPagos);
      setDatosLlaveEnMano({ error: "Error cargando pagos" });
      return;
    }

    // Calcular con nueva lógica
    const resultado = await calcularConSistemaActualizacion(
      proyecto.monto_total_llave_en_mano,
      proyecto.moneda_llave_en_mano,
      proyecto.metodo_actualizacion,
      proyecto.fecha_inicio_actualizacion_llave_en_mano,
      pagos || [],
      "llave_en_mano",
      !!proyecto.metodo_actualizacion
    );

    setDatosLlaveEnMano({
      montoInicial: proyecto.monto_total_llave_en_mano,
      monedaInicial: proyecto.moneda_llave_en_mano,
      metodoActualizacion: proyecto.metodo_actualizacion,
      fechaInicioActualizacion:
        proyecto.fecha_inicio_actualizacion_llave_en_mano,
      seActualiza: !!proyecto.metodo_actualizacion,
      pagos: pagos || [],
      ...resultado,
    });
  };

  const cargarDatosHonorarios = async () => {
    const proyecto = proyectoSeleccionado;

    let montoInicial = proyecto.honorarios || 0;
    let porcentaje = 0;
    let totalGastado = 0;
    let detalleCalculo = "";
    let unidadesTotalesCalculadas = 0;
    const gastosConUnidades = [];
    
    if (proyecto.tipo_obra === "administracion_porcentaje") {
      // Parsear porcentaje robustamente
      let rawPorcentaje = proyecto.porcentaje_honorario;
      if (typeof rawPorcentaje === "string") {
        rawPorcentaje = rawPorcentaje.replace(/,/g, ".");
      }
      porcentaje = parseFloat(rawPorcentaje) || 0;
      
      const seActualiza = proyecto.honorarios_se_actualizan;
      const metodoActualizacion = proyecto.metodo_actualizacion_honorarios;
      const monedaHonorarios = proyecto.moneda_honorarios;
      
      // Sumar todas las salidas del proyecto
      const { data: salidas, error: errorSalidas } = await supabase
        .from("salidas")
        .select("monto, moneda, tipo_cambio, fecha")
        .eq("proyecto_id", proyecto.id)
        .eq('activo', true)
        .is('deleted_at', null);
        
      if (seActualiza && metodoActualizacion && salidas && salidas.length > 0) {
        // CON CAC: Calcular unidades por cada salida
        for (const salida of salidas) {
          // 1. Convertir a moneda de honorarios
          const montoEnMonedaHonorarios = convertirMonto(
            salida.monto,
            salida.moneda,
            monedaHonorarios,
            salida.tipo_cambio
          );
          // 2. Aplicar porcentaje
          const honorarioSalida = montoEnMonedaHonorarios * (porcentaje / 100);
          // 3. Obtener índice de la fecha de la salida
          const indiceSalida = await obtenerIndicePorFecha(
            formatearFechaParaIndice(new Date(formatearFechaSinTimezone(salida.fecha) + "T00:00:00")),
            metodoActualizacion,
            "honorarios"
          );
          // 4. Calcular unidades
          const unidadesSalida = indiceSalida ? honorarioSalida / indiceSalida : 0;
          unidadesTotalesCalculadas += unidadesSalida;
          
          gastosConUnidades.push({
            ...salida,
            tipo: "salida",
            montoOriginal: salida.monto,
            montoConvertido: montoEnMonedaHonorarios,
            porcentajeAplicado: porcentaje,
            honorarioCalculado: honorarioSalida,
            indiceUsado: indiceSalida,
            unidades: unidadesSalida
          });
          
          totalGastado += montoEnMonedaHonorarios;
        }
      } else if (!errorSalidas && salidas) {
        // SIN CAC: Sumar directamente
        salidas.forEach((s) => {
          totalGastado += convertirMonto(
            s.monto,
            s.moneda,
            monedaHonorarios,
            s.tipo_cambio
          );
        });
      }
      
      // Obtener cajas de mano de obra del proyecto
      const { data: cajasManoObra } = await supabase
        .from("cajas")
        .select("id")
        .eq("proyecto_id", proyecto.id)
        .eq("tipo_caja", "mano_obra");
      const idsCajasManoObra = cajasManoObra?.map((c) => c.id) || [];
      
      // Sumar todas las entradas de mano de obra
      let totalEntradasMO = 0;
      if (idsCajasManoObra.length > 0) {
        const { data: entradasMO, error: errorEntradasMO } = await supabase
          .from("entradas")
          .select("monto, moneda, tipo_cambio, caja_destino_id, fecha")
          .eq("proyecto_id", proyecto.id)
          .in("caja_destino_id", idsCajasManoObra)
          .eq('activo', true)
          .is('deleted_at', null);
          
        if (seActualiza && metodoActualizacion && !errorEntradasMO && entradasMO) {
          // CON CAC: Calcular unidades por cada entrada de MO
          for (const entrada of entradasMO) {
            // 1. Convertir a moneda de honorarios
            const montoEnMonedaHonorarios = convertirMonto(
              entrada.monto,
              entrada.moneda,
              monedaHonorarios,
              entrada.tipo_cambio
            );
            // 2. Aplicar porcentaje
            const honorarioEntrada = montoEnMonedaHonorarios * (porcentaje / 100);
            // 3. Obtener índice de la fecha
            const indiceEntrada = await obtenerIndicePorFecha(
              formatearFechaParaIndice(new Date(formatearFechaSinTimezone(entrada.fecha) + "T00:00:00")),
              metodoActualizacion,
              "honorarios"
            );
            // 4. Calcular unidades
            const unidadesEntrada = indiceEntrada ? honorarioEntrada / indiceEntrada : 0;
            unidadesTotalesCalculadas += unidadesEntrada;
            
            gastosConUnidades.push({
              ...entrada,
              tipo: "entrada_mo",
              montoOriginal: entrada.monto,
              montoConvertido: montoEnMonedaHonorarios,
              porcentajeAplicado: porcentaje,
              honorarioCalculado: honorarioEntrada,
              indiceUsado: indiceEntrada,
              unidades: unidadesEntrada
            });
            
            totalEntradasMO += montoEnMonedaHonorarios;
          }
        } else if (!errorEntradasMO && entradasMO) {
          // SIN CAC: Sumar directamente
          entradasMO.forEach((entrada) => {
            totalEntradasMO += convertirMonto(
              entrada.monto,
              entrada.moneda,
              monedaHonorarios,
              entrada.tipo_cambio
            );
          });
        }
      }
      
      // Calcular total base para honorarios
      const totalBase = totalGastado + totalEntradasMO;
      montoInicial = (totalBase * porcentaje) / 100;
      if (!porcentaje || isNaN(montoInicial)) montoInicial = 0;
      detalleCalculo = `${totalBase.toLocaleString("es-AR", { maximumFractionDigits: 2 })} x ${porcentaje}% = $${montoInicial.toLocaleString("es-AR", { maximumFractionDigits: 2 })}`;
    } else {
      if (!proyecto.honorarios) {
        setDatosHonorarios({ error: "No hay honorarios configurados" });
        return;
      }
    }

    // Obtener pagos de honorarios
    const { data: pagos, error: errorPagos } = await supabase
      .from("entradas")
      .select(
        "fecha, monto, moneda, tipo_cambio, concepto_id, comprobante_url, tiene_comprobante, conceptos_entrada!inner(nombre)"
      )
      .eq("proyecto_id", proyecto.id)
      .ilike("conceptos_entrada.nombre", "%honorario%")
      .eq('activo', true)
      .is('deleted_at', null)
      .order("fecha", { ascending: true });

    if (errorPagos) {
      console.error("Error cargando pagos honorarios:", errorPagos);
      setDatosHonorarios({ error: "Error cargando pagos" });
      return;
    }

    let resultado;
    if (proyecto.tipo_obra === "administracion_porcentaje" && proyecto.honorarios_se_actualizan && proyecto.metodo_actualizacion_honorarios) {
      // Para porcentaje con CAC: usar función especializada
      resultado = await calcularConSistemaActualizacionPorcentajeAdicionalesShared(
        unidadesTotalesCalculadas,
        gastosConUnidades,
        proyecto.moneda_honorarios,
        proyecto.metodo_actualizacion_honorarios,
        pagos || [],
        "honorarios",
        obtenerIndicePorFecha,
        obtenerUltimoIndiceRealDisponible,
        supabase,
        proyecto.id
      );
    } else {
      // Para monto fijo o sin CAC: usar función estándar
      resultado = await calcularConSistemaActualizacion(
        montoInicial,
        proyecto.moneda_honorarios,
        proyecto.metodo_actualizacion_honorarios,
        proyecto.fecha_inicio_actualizacion_honorarios,
        pagos || [],
        "honorarios",
        proyecto.honorarios_se_actualizan
      );
    }

    setDatosHonorarios({
      montoInicial: montoInicial,
      monedaInicial: proyecto.moneda_honorarios,
      metodoActualizacion: proyecto.metodo_actualizacion_honorarios,
      fechaInicioActualizacion: proyecto.fecha_inicio_actualizacion_honorarios,
      seActualiza: proyecto.honorarios_se_actualizan,
      pagos: pagos || [],
      detalleCalculo: detalleCalculo,
      gastosConUnidades: gastosConUnidades, // Guardar para visualización
      esPorcentaje: proyecto.tipo_obra === "administracion_porcentaje",
      ...resultado,
    });
  };

  const cargarDatosManoObra = async () => {
    const proyecto = proyectoSeleccionado;

    if (!proyecto.monto_mano_obra) {
      setDatosManoObra({ error: "No hay monto de mano de obra configurado" });
      return;
    }

    // Obtener cajas de mano de obra del proyecto
    const { data: cajasManoObra } = await supabase
      .from("cajas")
      .select("id")
      .eq("proyecto_id", proyecto.id)
      .eq("tipo_caja", "mano_obra");

    const idsCajasManoObra = cajasManoObra?.map((c) => c.id) || [];

    let pagos = [];
    if (idsCajasManoObra.length > 0) {
      const { data: pagosMO, error: errorPagos } = await supabase
        .from("entradas")
        .select(
          "fecha, monto, moneda, tipo_cambio, concepto_id, comprobante_url, tiene_comprobante, conceptos_entrada!inner(nombre)"
        )
        .eq("proyecto_id", proyecto.id)
        .in("caja_destino_id", idsCajasManoObra)
        .eq('activo', true)
        .is('deleted_at', null)
        .order("fecha", { ascending: true });

      if (!errorPagos) {
        pagos = pagosMO || [];
      }
    }

    const resultado = await calcularConSistemaActualizacion(
      proyecto.monto_mano_obra,
      proyecto.moneda_mano_obra,
      proyecto.metodo_actualizacion_mano_obra,
      proyecto.fecha_inicio_actualizacion_mano_obra,
      pagos,
      "mano_obra",
      proyecto.mano_obra_se_actualiza
    );

    setDatosManoObra({
      montoInicial: proyecto.monto_mano_obra,
      monedaInicial: proyecto.moneda_mano_obra,
      metodoActualizacion: proyecto.metodo_actualizacion_mano_obra,
      fechaInicioActualizacion: proyecto.fecha_inicio_actualizacion_mano_obra,
      seActualiza: proyecto.mano_obra_se_actualiza,
      pagos: pagos,
      ...resultado,
    });
  };

  // ✅ NUEVA FUNCIÓN: Cargar datos de adicionales
  const cargarDatosAdicionales = async () => {
    const proyecto = proyectoSeleccionado;

    if (!proyecto.adicionales_honorarios_habilitado) {
      setDatosAdicionales(null);
      return;
    }

    console.log("🔍 DEBUG - HonorariosMO: Cargando datos adicionales", {
      proyectoId: proyecto.id,
      adicionalesHabilitado: proyecto.adicionales_honorarios_habilitado
    });

    try {
      // 1. Cargar gastos adicionales
      const { data: salidasAdicionales, error: errorSalidasAdicionales } = await supabase
        .from("salidas")
        .select("monto, moneda, tipo_cambio, fecha")
        .eq("constructora_id", constructora.id)
        .eq("proyecto_id", proyecto.id)
        .eq("es_adicional", true)
        .eq("activo", true)
        .is("deleted_at", null);

      if (errorSalidasAdicionales) {
        console.error("Error cargando salidas adicionales:", errorSalidasAdicionales);
        setDatosAdicionales({ error: "Error cargando gastos adicionales" });
        return;
      }

      // Calcular total de gastos adicionales (para mostrar, no para honorarios)
      let totalGastosAdicionales = 0;
      if (salidasAdicionales && salidasAdicionales.length > 0) {
        for (const salida of salidasAdicionales) {
          const montoConvertido = convertirMonto(salida.monto, salida.moneda, "USD", salida.tipo_cambio);
          totalGastosAdicionales += montoConvertido;
        }
      }

      // 2. Calcular honorarios por adicionales CORRECTAMENTE
      let honorariosCalculados = 0;
      const metodoActualizacion = proyecto.adicionales_metodo_actualizacion;
      const seActualiza = proyecto.adicionales_actualizar_con_indice;
      const monedaHonorarios = proyecto.adicionales_moneda_honorarios || "USD";

      // Para porcentaje con CAC, necesitamos calcular unidades por salida
      let unidadesTotalesCalculadas = 0;
      const salidasConUnidades = [];

      if (proyecto.adicionales_tipo_honorarios === "porcentaje") {
        const porcentaje = parseFloat(proyecto.adicionales_porcentaje_honorarios || 0);
        
        if (seActualiza && metodoActualizacion) {
          // CON CAC: Calcular unidades por cada salida
          for (const salida of salidasAdicionales || []) {
            // 1. Convertir salida a moneda de honorarios
            const montoSalidaEnMonedaHonorarios = convertirMonto(salida.monto, salida.moneda, monedaHonorarios, salida.tipo_cambio);
            // 2. Aplicar porcentaje
            const honorarioSalida = montoSalidaEnMonedaHonorarios * (porcentaje / 100);
            // 3. Obtener índice de la fecha de la salida
            const indiceSalida = await obtenerIndicePorFecha(
              formatearFechaParaIndice(new Date(formatearFechaSinTimezone(salida.fecha) + "T00:00:00")),
              metodoActualizacion,
              "adicionales"
            );
            // 4. Calcular unidades
            const unidadesSalida = indiceSalida ? honorarioSalida / indiceSalida : 0;
            unidadesTotalesCalculadas += unidadesSalida;
            
            salidasConUnidades.push({
              ...salida,
              montoOriginal: salida.monto,
              montoConvertido: montoSalidaEnMonedaHonorarios,
              porcentajeAplicado: porcentaje,
              honorarioCalculado: honorarioSalida,
              indiceUsado: indiceSalida,
              unidades: unidadesSalida
            });
          }
          // El monto inicial en pesos es la suma de honorarios calculados
          honorariosCalculados = salidasAdicionales.reduce((sum, salida) => {
            const montoEnMonedaHonorarios = convertirMonto(salida.monto, salida.moneda, monedaHonorarios, salida.tipo_cambio);
            return sum + (montoEnMonedaHonorarios * (porcentaje / 100));
          }, 0);
        } else {
          // SIN CAC: Sumar directamente
          for (const salida of salidasAdicionales || []) {
            const montoSalidaEnMonedaHonorarios = convertirMonto(salida.monto, salida.moneda, monedaHonorarios, salida.tipo_cambio);
            const honorarioSalida = montoSalidaEnMonedaHonorarios * (porcentaje / 100);
            honorariosCalculados += honorarioSalida;
          }
        }
      } else if (proyecto.adicionales_tipo_honorarios === "monto_fijo") {
        const montoFijo = parseFloat(proyecto.adicionales_monto_fijo_honorarios || 0);
        honorariosCalculados = montoFijo;
      }

      // 3. Cargar pagos de honorarios por adicionales
      const { data: conceptos, error: errorConceptos } = await supabase
        .from("conceptos_entrada")
        .select("id")
        .or("nombre.ilike.%Honorarios por Adicionales%,nombre.ilike.%Honorarios por adicionales%")
        .single();

      let pagos = [];
      if (!errorConceptos && conceptos) {
        const { data: entradasAdicionales, error: errorEntradas } = await supabase
          .from("entradas")
          .select("fecha, monto, moneda, tipo_cambio, concepto_id, comprobante_url, tiene_comprobante")
          .eq("constructora_id", constructora.id)
          .eq("proyecto_id", proyecto.id)
          .eq("concepto_id", conceptos.id)
          .eq("activo", true)
          .is("deleted_at", null)
          .order("fecha", { ascending: true });

        if (!errorEntradas) {
          pagos = entradasAdicionales || [];
        }
      }

      // 4. Calcular con sistema de actualización si está habilitado
      let resultado = {
        evolucion: [{
          mes: "Honorarios Adicionales",
          fecha: new Date().toISOString().split("T")[0],
          montoInicial: honorariosCalculados,
          totalPagado: pagos.reduce((sum, pago) => 
            sum + convertirMonto(pago.monto, pago.moneda, monedaHonorarios, pago.tipo_cambio), 0),
          saldoPendiente: honorariosCalculados - pagos.reduce((sum, pago) => 
            sum + convertirMonto(pago.monto, pago.moneda, monedaHonorarios, pago.tipo_cambio), 0),
          observaciones: "Cálculo directo"
        }],
        resumenFinal: {
          montoActualizado: honorariosCalculados,
          pagosAcumulados: pagos.reduce((sum, pago) => 
            sum + convertirMonto(pago.monto, pago.moneda, monedaHonorarios, pago.tipo_cambio), 0),
          saldoPendiente: honorariosCalculados - pagos.reduce((sum, pago) => 
            sum + convertirMonto(pago.monto, pago.moneda, monedaHonorarios, pago.tipo_cambio), 0),
        }
      };

      if (seActualiza && metodoActualizacion && honorariosCalculados > 0) {
        if (proyecto.adicionales_tipo_honorarios === "porcentaje") {
          // Para porcentaje: usamos las unidades ya calculadas por salida
          resultado = await calcularConSistemaActualizacionPorcentajeAdicionalesShared(
            unidadesTotalesCalculadas,
            salidasConUnidades,
            monedaHonorarios,
            metodoActualizacion,
            pagos,
            "adicionales",
            obtenerIndicePorFecha,
            obtenerUltimoIndiceRealDisponible,
            supabase,
            proyecto.id
          );
        } else {
          // Para monto fijo: usamos la lógica estándar
          resultado = await calcularConSistemaActualizacion(
            honorariosCalculados,
            monedaHonorarios,
            metodoActualizacion,
            (proyecto.adicionales_fecha_inicio_actualizacion || proyecto.fecha_inicio || new Date().toISOString().split("T")[0]),
            pagos,
            "adicionales",
            seActualiza
          );
        }
      }

      setDatosAdicionales({
        montoInicial: honorariosCalculados,
        monedaInicial: monedaHonorarios,
        metodoActualizacion: metodoActualizacion,
        fechaInicioActualizacion: (proyecto.adicionales_fecha_inicio_actualizacion || proyecto.fecha_inicio || new Date().toISOString().split("T")[0]),
        seActualiza: seActualiza,
        pagos: pagos,
        totalGastosAdicionales: totalGastosAdicionales,
        salidasConUnidades: salidasConUnidades, // Guardar para debug
        esPorcentaje: proyecto.adicionales_tipo_honorarios === "porcentaje", // Marcar si es porcentaje
        ...resultado,
      });

    } catch (error) {
      console.error("Error cargando datos adicionales:", error);
      setDatosAdicionales({ error: "Error cargando datos de adicionales" });
    }
  };

  // FUNCIÓN PRINCIPAL: CALCULAR CON SISTEMA DE ACTUALIZACIÓN
  const calcularConSistemaActualizacion = async (
    montoInicial,
    monedaInicial,
    metodoActualizacion,
    fechaInicio,
    pagos,
    tipo,
    seActualiza
  ) => {
    console.log(`🔄 CALCULANDO CON SISTEMA:`, {
      montoInicial,
      seActualiza,
      metodoActualizacion,
      tipo,
      esPersonalizado: esMetodoPersonalizado(metodoActualizacion),
    });

    // SI NO SE ACTUALIZA: Cálculo simple sin índices
    if (!seActualiza || !metodoActualizacion || !fechaInicio) {
      console.log(`❌ SIN ACTUALIZACIÓN - Cálculo directo`);

      const totalPagadoConvertido = pagos.reduce((sum, pago) => {
        return (
          sum +
          convertirMonto(
            pago.monto,
            pago.moneda,
            monedaInicial,
            pago.tipo_cambio
          )
        );
      }, 0);

      return {
        evolucion: [
          {
            mes: "Presupuesto",
            fecha: new Date().toISOString().split("T")[0],
            montoInicial: parseFloat(montoInicial),
            totalPagado: totalPagadoConvertido,
            saldoPendiente: parseFloat(montoInicial) - totalPagadoConvertido,
            observaciones: "Sin actualización configurada",
          },
        ],
        resumenFinal: {
          montoActualizado: parseFloat(montoInicial),
          pagosAcumulados: totalPagadoConvertido,
          saldoPendiente: parseFloat(montoInicial) - totalPagadoConvertido,
        },
      };
    }

    // CON ACTUALIZACIÓN: Lógica de unidades
    console.log(`✅ CON ACTUALIZACIÓN - Sistema: ${metodoActualizacion}`);

    // CORREGIR FECHA SIN TIMEZONE
    const fechaInicioSinTZ = formatearFechaSinTimezone(fechaInicio);
    const fechaInicioDate = new Date(fechaInicioSinTZ + "T00:00:00");
    const mesInicio = formatearFechaParaIndice(fechaInicioDate);

    console.log(
      `📅 FECHA INICIO CORREGIDA: ${fechaInicio} → ${fechaInicioSinTZ} → ${mesInicio}`
    );

    // 1. OBTENER ÍNDICE INICIAL (del mes de inicio del presupuesto)
    const indiceInicial = await obtenerIndicePorFecha(
      mesInicio,
      metodoActualizacion,
      tipo
    );

    if (!indiceInicial) {
      if (esMetodoPersonalizado(metodoActualizacion)) {
        console.log(
          `⚠️ Método personalizado "${metodoActualizacion}" sin valor para ${mesInicio} - USANDO VALOR POR DEFECTO`
        );

        // USAR VALOR POR DEFECTO PARA SISTEMAS PERSONALIZADOS
        const indiceDefecto = 1000; // Valor base para comenzar

        return {
          seActualiza: true,
          metodoActualizacion: metodoActualizacion,
          requiereConfiguracion: true,
          indiceDefectoUsado: indiceDefecto,
          evolucion: [
            {
              mes: "Configuración Pendiente",
              fecha: mesInicio,
              observaciones: `Sistema "${metodoActualizacion}" requiere configuración de índices. Se usa valor temporal de ${indiceDefecto}.`,
            },
          ],
          resumenFinal: {
            montoActualizado: parseFloat(montoInicial),
            pagosAcumulados: pagos.reduce(
              (sum, pago) =>
                sum +
                convertirMonto(
                  pago.monto,
                  pago.moneda,
                  monedaInicial,
                  pago.tipo_cambio
                ),
              0
            ),
            saldoPendiente:
              parseFloat(montoInicial) -
              pagos.reduce(
                (sum, pago) =>
                  sum +
                  convertirMonto(
                    pago.monto,
                    pago.moneda,
                    monedaInicial,
                    pago.tipo_cambio
                  ),
                0
              ),
            unidades: parseFloat(montoInicial) / indiceDefecto,
            ultimoIndiceUsado: indiceDefecto,
            totalUnidades: parseFloat(montoInicial) / indiceDefecto,
            unidadesGastadas: 0,
          },
        };
      } else {
        console.error(`❌ NO SE ENCONTRÓ ÍNDICE CAC OFICIAL para ${mesInicio}`);
        return {
          error: `No se encontró índice CAC para ${mesInicio} con el método ${metodoActualizacion}.`,
        };
      }
    }

    console.log(
      `📊 ÍNDICE INICIAL (${mesInicio}): ${indiceInicial.toFixed(2)}`
    );

    // 2. CONVERTIR PRESUPUESTO A UNIDADES
    const unidadesSistema = parseFloat(montoInicial) / indiceInicial;
    console.log(`🔢 UNIDADES SISTEMA: ${unidadesSistema.toFixed(2)} unidades`);

    // 3. PROCESAR PAGOS MES A MES
    const evolucion = [];
    const pagosPorMes = agruparPagosPorMes(pagos, monedaInicial);

    let unidadesGastadas = 0;
    const fechaHoy = new Date();
    let mesActual = new Date(
      fechaInicioDate.getFullYear(),
      fechaInicioDate.getMonth(),
      1
    );

    // OBTENER EL ÚLTIMO ÍNDICE REAL DISPONIBLE PARA CONSISTENCIA
    const ultimoIndiceReal = await obtenerUltimoIndiceRealDisponible(
      metodoActualizacion,
      tipo
    );

    while (mesActual <= fechaHoy) {
      const mesString = formatearFechaParaIndice(mesActual);
      const pagosDelMes = pagosPorMes[mesString] || [];

      // Índice para este mes
      let indiceDelMes = await obtenerIndicePorFecha(
        mesString,
        metodoActualizacion,
        tipo
      );
      let esProvisorio = false;
      let esEditado = false;

      // Verificar si hay valor personalizado/editado para este mes
      try {
        const { data: valorPersonalizado } = await supabase
          .from("valores_personalizados_actualizacion")
          .select("coeficiente")
          .eq("proyecto_id", proyectoSeleccionado.id)
          .eq("tipo", tipo)
          .eq("fecha_aplicacion", mesString)
          .maybeSingle();

        if (valorPersonalizado) {
          esEditado = true;
        }
      } catch (err) {
        // Ignorar errores
      }

      // Verificar si hay índice oficial para este mes específico
      let hayIndiceOficialParaEsteMes = false;
      if (!esMetodoPersonalizado(metodoActualizacion)) {
        const indiceOficial = indicesCAC.find((i) => i.fecha === mesString);
        hayIndiceOficialParaEsteMes = !!indiceOficial;
      }

      // Si no hay índice para este mes, usar el último real disponible (CONSISTENTE)
      if (!indiceDelMes) {
        indiceDelMes = ultimoIndiceReal;
        esProvisorio = true;
      } else if (!hayIndiceOficialParaEsteMes && !esEditado) {
        // Si hay índice pero no es oficial para este mes específico y no es editado, es provisorio
        esProvisorio = true;
      }

      if (!indiceDelMes) {
        console.error(`❌ NO SE ENCONTRÓ ÍNDICE para ${mesString}`);
        break;
      }

      // Convertir pagos del mes a unidades
      let unidadesDelMes = 0;
      const pagosDetallados = pagosDelMes.map((pago) => {
        const unidadesPago = pago.montoConvertido / indiceDelMes;
        unidadesDelMes += unidadesPago;
        return {
          ...pago,
          indiceUsado: indiceDelMes,
          unidades: unidadesPago,
          esProvisorio: esProvisorio,
          esEditado: esEditado,
        };
      });

      unidadesGastadas += unidadesDelMes;

      // Saldo en unidades y en pesos
      const unidadesRestantes = unidadesSistema - unidadesGastadas;
      const saldoEnPesos = unidadesRestantes * indiceDelMes;

      evolucion.push({
        mes: formatearMesAno(mesActual),
        fecha: mesString,
        indiceUsado: indiceDelMes,
        esProvisorio: esProvisorio,
        esEditado: esEditado,
        unidadesDelMes: unidadesDelMes,
        unidadesGastadas: unidadesGastadas,
        unidadesRestantes: unidadesRestantes,
        saldoEnPesos: saldoEnPesos,
        pagosDelMes: pagosDetallados,
        cantidadPagos: pagosDelMes.length,
        observaciones: esEditado 
          ? `Índice personalizado/editado`
          : esProvisorio
          ? `Índice provisorio (último disponible)`
          : `Índice oficial del mes`,
      });

      mesActual.setMonth(mesActual.getMonth() + 1);
    }

    // 4. CALCULAR TOTALES FINALES
    const ultimoIndiceDisponible = ultimoIndiceReal;

    // Recalcular saldo final con el último índice disponible
    const unidadesFinales = unidadesSistema - unidadesGastadas;
    const saldoFinalEnPesos = unidadesFinales * ultimoIndiceDisponible;
    const totalPagadoEnPesos = pagos.reduce((sum, pago) => {
      return (
        sum +
        convertirMonto(pago.monto, pago.moneda, monedaInicial, pago.tipo_cambio)
      );
    }, 0);

    console.log(`💰 RESULTADO FINAL:`, {
      unidadesSistema: unidadesSistema.toFixed(2),
      unidadesGastadas: unidadesGastadas.toFixed(2),
      unidadesRestantes: unidadesFinales.toFixed(2),
      ultimoIndice: ultimoIndiceDisponible.toFixed(2),
      saldoFinalEnPesos: saldoFinalEnPesos.toFixed(0),
    });

    return {
      unidadesSistema: unidadesSistema,
      ultimoIndiceDisponible: ultimoIndiceDisponible,
      evolucion: evolucion,
      resumenFinal: {
        montoActualizado: unidadesFinales * ultimoIndiceDisponible,
        pagosAcumulados: totalPagadoEnPesos,
        saldoPendiente: saldoFinalEnPesos,
        unidades: unidadesFinales,
        ultimoIndiceUsado: ultimoIndiceDisponible,
        totalUnidades: unidadesSistema,
        unidadesGastadas: unidadesGastadas,
      },
    };
  };

  // FUNCIÓN: OBTENER ÍNDICE POR FECHA (CAC oficial o personalizado) - CORREGIDA
  const obtenerIndicePorFecha = async (fecha, metodo, tipo) => {
    if (!metodo) return null;

    console.log(
      `🔍 BUSCANDO ÍNDICE: fecha=${fecha}, metodo=${metodo}, tipo=${tipo}, esPersonalizado=${esMetodoPersonalizado(
        metodo
      )}`
    );

    // PRIMERO: Buscar si hay valor personalizado (override)
    try {
      const { data: valorPersonalizado, error } = await supabase
        .from("valores_personalizados_actualizacion")
        .select("coeficiente")
        .eq("proyecto_id", proyectoSeleccionado.id)
        .eq("tipo", tipo)
        .eq("fecha_aplicacion", fecha)
        .maybeSingle();

      if (!error && valorPersonalizado) {
        console.log(
          `🎛️ VALOR PERSONALIZADO encontrado para ${fecha}:`,
          valorPersonalizado.coeficiente
        );
        return parseFloat(valorPersonalizado.coeficiente);
      }
    } catch (err) {
      console.error("Error buscando valor personalizado:", err);
    }

    // Si es método personalizado pero no hay valor, usar último disponible
    if (esMetodoPersonalizado(metodo)) {
      console.log(
        `⚠️ Método personalizado "${metodo}" pero no hay valor configurado para ${fecha}`
      );
      const ultimoPersonalizado = await obtenerUltimoIndiceRealDisponible(
        metodo,
        tipo
      );
      if (ultimoPersonalizado) {
        console.log(
          `↩️ Usando ÚLTIMO PERSONALIZADO disponible como fallback: ${ultimoPersonalizado}`
        );
        return ultimoPersonalizado;
      }
      return null;
    }

    // Buscar en índices CAC oficiales
    const indice = indicesCAC.find((i) => i.fecha === fecha);
    if (!indice) {
      console.log(`❌ No hay CAC oficial para ${fecha}`);
      // Fallback: usar el último CAC disponible
      const ultimo = await obtenerUltimoIndiceRealDisponible(metodo, tipo);
      if (ultimo) {
        console.log(`↩️ Usando ÚLTIMO CAC disponible como fallback: ${ultimo}`);
        return ultimo;
      }
      return null;
    }

    let valor = null;
    switch (metodo) {
      case "CAC_GENERAL":
        valor = parseFloat(indice.cac_general);
        break;
      case "CAC_MATERIALES":
        valor = parseFloat(indice.cac_materiales);
        break;
      case "CAC_MANO_OBRA":
        valor = parseFloat(indice.cac_mano_obra);
        break;
    }

    console.log(`📊 CAC OFICIAL para ${fecha} (${metodo}): ${valor}`);
    return valor;
  };

  // FUNCIÓN: OBTENER ÚLTIMO ÍNDICE REAL DISPONIBLE (CORREGIDA)
  const obtenerUltimoIndiceRealDisponible = async (metodo, tipo) => {
    console.log(
      `🔍 BUSCANDO ÚLTIMO ÍNDICE DISPONIBLE: metodo=${metodo}, tipo=${tipo}, esPersonalizado=${esMetodoPersonalizado(
        metodo
      )}`
    );

    if (esMetodoPersonalizado(metodo)) {
      // Para personalizados, buscar el último valor cargado
      try {
        const { data: ultimoPersonalizado, error } = await supabase
          .from("valores_personalizados_actualizacion")
          .select("coeficiente, fecha_aplicacion")
          .eq("proyecto_id", proyectoSeleccionado.id)
          .eq("tipo", tipo)
          .order("fecha_aplicacion", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (!error && ultimoPersonalizado) {
          console.log(
            `🔍 ÚLTIMO PERSONALIZADO: ${ultimoPersonalizado.fecha_aplicacion} = ${ultimoPersonalizado.coeficiente}`
          );
          return parseFloat(ultimoPersonalizado.coeficiente);
        }
      } catch (err) {
        console.error("Error buscando último personalizado:", err);
      }

      console.log(
        `❌ NO SE ENCONTRÓ NINGÚN VALOR PERSONALIZADO para método ${metodo}`
      );
      return null;
    }

    // Para CAC, buscar el último disponible
    const indicesOrdenados = [...indicesCAC].sort(
      (a, b) => new Date(b.fecha) - new Date(a.fecha)
    );

    for (const indice of indicesOrdenados) {
      let valor = null;
      switch (metodo) {
        case "CAC_GENERAL":
          valor = parseFloat(indice.cac_general);
          break;
        case "CAC_MATERIALES":
          valor = parseFloat(indice.cac_materiales);
          break;
        case "CAC_MANO_OBRA":
          valor = parseFloat(indice.cac_mano_obra);
          break;
      }

      if (valor) {
        console.log(
          `🔍 ÚLTIMO CAC DISPONIBLE: ${indice.fecha} = ${valor.toFixed(2)}`
        );
        return valor;
      }
    }

    console.error(`❌ NO SE ENCONTRÓ NINGÚN ÍNDICE CAC DISPONIBLE`);
    return null;
  };

  // FUNCIÓN ESPECIAL centralizada: usar util compartido
  const calcularConSistemaActualizacionPorcentajeAdicionales =
    calcularConSistemaActualizacionPorcentajeAdicionalesShared;

  // FUNCIÓN: AGRUPAR PAGOS POR MES
  const agruparPagosPorMes = (pagos, monedaDestino) => {
    const grupos = {};

    pagos.forEach((pago) => {
      const fechaPago = new Date(
        formatearFechaSinTimezone(pago.fecha) + "T00:00:00"
      );
      const mesString = formatearFechaParaIndice(fechaPago);

      if (!grupos[mesString]) {
        grupos[mesString] = [];
      }

      grupos[mesString].push({
        ...pago,
        montoConvertido: convertirMonto(
          pago.monto,
          pago.moneda,
          monedaDestino,
          pago.tipo_cambio
        ),
      });
    });

    return grupos;
  };

  const formatearFechaParaIndice = (fecha) => {
    return fecha.toISOString().split("T")[0].substring(0, 7) + "-01";
  };

  const formatearMesAno = (fecha) => {
    return new Intl.DateTimeFormat("es-ES", {
      year: "numeric",
      month: "short",
    }).format(fecha);
  };

  // FUNCIÓN: FORMATEAR MONEDA (CORREGIDA PARA EVITAR ERRORES)
  const formatCurrency = (valor, moneda) => {
    // Asegurar que valor sea un número válido
    const valorNumerico = parseFloat(valor || 0);

    if (isNaN(valorNumerico)) {
      console.warn("formatCurrency recibió un valor inválido:", valor);
      return "$0.00";
    }

    const simbolo = moneda === "USD" ? "US$" : "$";
    return `${simbolo}${valorNumerico.toLocaleString("es-AR", {
      maximumFractionDigits: 2,
    })}`;
  };

  // FUNCIÓN: OBTENER NOMBRE AMIGABLE DEL MÉTODO (MEJORADA)
  const obtenerNombreMetodo = (metodo) => {
    if (!metodo) return "No especificado";

    switch (metodo) {
      case "CAC_GENERAL":
        return "CAC General";
      case "CAC_MATERIALES":
        return "CAC Materiales";
      case "CAC_MANO_OBRA":
        return "CAC Mano de Obra";
      default:
        // Si no es ninguno de los 3 oficiales, es personalizado
        return `Sistema "${metodo}"`;
    }
  };

  const [resumenHonorariosPorcentaje, setResumenHonorariosPorcentaje] = useState({
    totalGastado: 0,
    totalPagado: 0,
    porcentaje: 0,
    totalHonorarios: 0,
    saldo: 0,
    cargando: false,
    error: null,
  });

  useEffect(() => {
    if (
      proyectoSeleccionado &&
      proyectoSeleccionado.tipo_obra === "administracion_porcentaje"
    ) {
      setResumenHonorariosPorcentaje((prev) => ({ ...prev, cargando: true, error: null }));
      (async () => {
        try {
          // 1. Total gastado: suma de salidas del proyecto
          let totalGastado = 0;
          const { data: salidas, error: errorSalidas } = await supabase
            .from("salidas")
            .select("monto, moneda, tipo_cambio")
            .eq("proyecto_id", proyectoSeleccionado.id)
            .eq('activo', true)
            .is('deleted_at', null);
          if (errorSalidas) throw new Error("Error cargando salidas");
          if (salidas) {
            salidas.forEach((s) => {
              const monto = parseFloat(s.monto || 0);
              const tc = parseFloat(s.tipo_cambio || 1);
              totalGastado += s.moneda === "USD" ? monto * tc : monto;
            });
          }

          // 2. Total pagado como honorarios: suma de entradas con concepto honorario
          let totalPagado = 0;
          const { data: entradas, error: errorEntradas } = await supabase
            .from("entradas")
            .select("monto, moneda, tipo_cambio, conceptos_entrada!inner(nombre)")
            .eq("proyecto_id", proyectoSeleccionado.id)
            .ilike("conceptos_entrada.nombre", "%honorario%")
            .eq('activo', true)
            .is('deleted_at', null);
          if (errorEntradas) throw new Error("Error cargando pagos honorarios");
          if (entradas) {
            entradas.forEach((e) => {
              const monto = parseFloat(e.monto || 0);
              const tc = parseFloat(e.tipo_cambio || 1);
              totalPagado += e.moneda === "USD" ? monto * tc : monto;
            });
          }

          // 3. Porcentaje configurado
          const porcentaje = parseFloat(
            proyectoSeleccionado.porcentaje_honorario?.toString().replace(",", ".") || 0
          );
          // 4. Total honorarios calculados
          const totalHonorarios = (totalGastado * porcentaje) / 100;
          // 5. Saldo
          const saldo = totalHonorarios - totalPagado;

          setResumenHonorariosPorcentaje({
            totalGastado,
            totalPagado,
            porcentaje,
            totalHonorarios,
            saldo,
            cargando: false,
            error: null,
          });
        } catch (err) {
          setResumenHonorariosPorcentaje((prev) => ({
            ...prev,
            cargando: false,
            error: err.message,
          }));
        }
      })();
    }
  }, [proyectoSeleccionado]);

  // PANTALLA DE CARGA MIENTRAS VERIFICA PERMISOS
  if (verificandoPermisos) {
    return (
      <div
        style={{
          padding: isMobile ? "16px 12px 120px 12px" : "20px",
          maxWidth: "100%",
          margin: "0 auto",
          background: "#f8fafc",
          minHeight: "100vh",
          animation: "fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div
          style={{
            textAlign: "center",
            padding: "60px 20px",
            color: "#64748b",
            fontSize: "16px",
          }}
        >
          <div
            style={{
              display: "inline-block",
              width: "32px",
              height: "32px",
              border: "3px solid #e2e8f0",
              borderTop: "3px solid #8b5cf6",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              marginBottom: "15px",
            }}
          ></div>
          <p>Verificando permisos...</p>
        </div>
      </div>
    );
  }

  // MOSTRAR ERROR SI EL CLIENTE NO PUEDE ACCEDER
  if (datosCliente?.esCliente && datosCliente?.error) {
    return (
      <div
        style={{
          padding: isMobile ? "16px 12px 120px 12px" : "20px",
          maxWidth: "100%",
          margin: "0 auto",
          background: "#f8fafc",
          minHeight: "100vh",
          animation: "fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div
          style={{
            background: "white",
            borderRadius: "16px",
            padding: isMobile ? "40px 20px" : "60px 40px",
            textAlign: "center",
            boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
            border: "2px solid #fecaca",
            animation: "slideInRight 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <div
            style={{
              width: "64px",
              height: "64px",
              background: "#fef2f2",
              borderRadius: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
            }}
          >
            <XCircle size={32} color="#dc2626" />
          </div>
          <h3
            style={{
              color: "#dc2626",
              marginBottom: "10px",
              fontSize: "20px",
              fontWeight: "600",
            }}
          >
            Acceso Denegado
          </h3>
          <p
            style={{ color: "#ef4444", marginBottom: "20px", fontSize: "15px" }}
          >
            {datosCliente.error}
          </p>
          <p style={{ color: "#64748b", fontSize: "14px" }}>
            Contacta a tu constructora si crees que esto es un error.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        style={{
          padding: isMobile ? "16px 12px 120px 12px" : "20px",
          maxWidth: "100%",
          margin: "0 auto",
          background: "#f8fafc",
          minHeight: "100vh",
          animation: "fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div
          style={{
            textAlign: "center",
            padding: "60px 20px",
            color: "#64748b",
            fontSize: "16px",
          }}
        >
          <div
            style={{
              display: "inline-block",
              width: "32px",
              height: "32px",
              border: "3px solid #e2e8f0",
              borderTop: "3px solid #8b5cf6",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              marginBottom: "15px",
            }}
          ></div>
          <p>Cargando proyectos...</p>
        </div>
      </div>
    );
  }

  if (!proyectoSeleccionado) {
    return (
      <div
        style={{
          padding: isMobile ? "16px 12px 120px 12px" : "20px",
          maxWidth: "100%",
          margin: "0 auto",
          background: "#f8fafc",
          minHeight: "100vh",
          animation: "fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div
          style={{
            background: "white",
            borderRadius: "16px",
            padding: isMobile ? "40px 20px" : "60px 40px",
            textAlign: "center",
            boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
            animation: "slideInRight 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <div
            style={{
              width: "64px",
              height: "64px",
              background: getVistaColor("honorarios-mo").light,
              borderRadius: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
            }}
          >
            <DollarSign
              size={32}
              color={getVistaColor("honorarios-mo").solid}
            />
          </div>
          <h3
            style={{
              color: "#1e293b",
              marginBottom: "10px",
              fontSize: "20px",
              fontWeight: "600",
            }}
          >
            No hay proyectos disponibles
          </h3>
          <p style={{ color: "#64748b", fontSize: "15px" }}>
            {datosCliente?.esCliente
              ? "No se encontró un proyecto asociado a tu email. Contacta a tu constructora."
              : "Crea tu primer proyecto para ver honorarios y mano de obra"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: isMobile ? "16px 12px 120px 12px" : "20px",
        maxWidth: "100%",
        margin: "0 auto",
        background: "#f8fafc",
        minHeight: "100vh",
        animation: "fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {/* Header unificado */}
      <div
        className="header-section"
        style={{
          marginBottom: "20px",
          animation: "slideInRight 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "16px",
            marginBottom: "16px",
            minHeight: "56px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flex: 1,
              minWidth: "200px",
            }}
          >
            <div
  style={{
    width: "40px",
    height: "40px",
    background: "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 4px 16px #2563eb40",
    flexShrink: 0,
  }}
>
  <DollarSign size={20} color="white" />
</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1
                style={{
                  fontSize: isMobile ? "20px" : "24px",
                  fontWeight: "700",
                  color: "#0f172a",
                  margin: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Honorarios y Mano de Obra
              </h1>
              <p
                style={{
                  fontSize: "14px",
                  color: "#64748b",
                  margin: 0,
                  fontWeight: "400",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Control detallado de pagos con sistemas de actualización
                {/* Ya no mostramos "Vista Cliente" al cliente */}
              </p>
            </div>
          </div>

          {/* Botón de recarga */}
          {proyectoSeleccionado && (
            <button
              onClick={async () => {
                setLoadingData(true);
                try {
                  // Recargar datos del proyecto desde la base de datos
                  const { data: proyectoActualizado, error } = await supabase
                    .from("proyectos")
                    .select("*")
                    .eq("id", proyectoSeleccionado.id)
                    .single();

                  if (!error && proyectoActualizado) {
                    setProyectoSeleccionado(proyectoActualizado);
                    guardarProyectoSeleccionado(proyectoActualizado);
                    await cargarDatosProyecto();
                    showCustomAlert(
                      "success",
                      "Actualizado",
                      "Datos del proyecto recargados correctamente"
                    );
                  }
                } catch (err) {
                  console.error("Error recargando proyecto:", err);
                  showCustomAlert("error", "Error", "Error al recargar datos");
                }
                setLoadingData(false);
              }}
              disabled={loadingData}
              style={{
                background: loadingData ? "#94a3b8" : "#2563eb",
                color: "white",
                border: "none",
                borderRadius: "10px",
                padding: isMobile ? "8px 12px" : "10px 16px",
                fontSize: "14px",
                fontWeight: "600",
                cursor: loadingData ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                boxShadow: "0 2px 8px rgba(37, 99, 235, 0.2)",
              }}
              className="button-hover"
            >
              <RefreshCw size={16} style={{ 
                animation: loadingData ? "spin 1s linear infinite" : "none" 
              }} />
              {!isMobile && "Actualizar"}
            </button>
          )}
        </div>

        {/* Selector de proyecto - SOLO MOSTRAR SI NO ES CLIENTE O SI ES EMPLEADO */}
        {(!datosCliente?.esCliente || proyectos.length > 1) && (
          <div
            style={{
              marginBottom: "20px",
            }}
          >
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "600",
                color: "#374151",
              }}
            >
              Proyecto
              {datosCliente?.esCliente && (
                <span
                  style={{
                    color: "#64748b",
                    fontWeight: "400",
                    marginLeft: "8px",
                  }}
                >
                  (Tu proyecto)
                </span>
              )}
            </label>
            <CustomDropdown
              value={proyectoSeleccionado?.id || ""}
              onChange={(value) => {
                // SOLO PERMITIR CAMBIO SI NO ES CLIENTE
                if (!datosCliente?.esCliente) {
                  const proyecto = proyectos.find((p) => p.id === value);
                  setProyectoSeleccionado(proyecto);
                  guardarProyectoSeleccionado(proyecto);
                }
              }}
              options={proyectos.map((proyecto) => ({
                value: proyecto.id,
                label: `${proyecto.codigo} - ${proyecto.apellido_cliente}${
                  proyecto.obra_finalizada ? " (Finalizado)" : ""
                }`,
              }))}
              placeholder="Selecciona un proyecto"
              disabled={datosCliente?.esCliente}
              icon={Building2}
            />
            {datosCliente?.esCliente && (
              <p
                style={{
                  fontSize: "12px",
                  color: "#64748b",
                  margin: "4px 0 0 0",
                  fontStyle: "italic",
                }}
              >
                Como cliente, solo puedes ver tu proyecto asignado
              </p>
            )}
          </div>
        )}
      </div>

      

      {loadingData ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "80px 20px",
            background: "white",
            borderRadius: "16px",
            border: "1px solid #f1f5f9",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                width: "32px",
                height: "32px",
                border: "2px solid #e2e8f0",
                borderTop: "2px solid #3b82f6",
                borderRadius: "50%",
                margin: "0 auto 16px",
                animation: "spin 1s linear infinite",
              }}
            />
            <p
              style={{
                margin: 0,
                fontSize: "14px",
                color: "#64748b",
                fontWeight: "500",
              }}
            >
              Cargando...
            </p>
          </div>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gap: "20px",
            animation: "fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {/* Llave en Mano */}
          {proyectoSeleccionado?.tipo_obra === "llave_en_mano" && (
            <TarjetaConcepto
              titulo="Costo Total de la Obra"
              datos={datosLlaveEnMano}
              cotizacionDolar={cotizacionDolar}
              isMobile={isMobile}
              tipo="llave_en_mano"
              formatCurrency={formatCurrency}
              obtenerNombreMetodo={obtenerNombreMetodo}
              convertirMonto={convertirMonto}
              esMetodoPersonalizado={esMetodoPersonalizado}
              proyectoSeleccionado={proyectoSeleccionado}
              indicesCAC={indicesCAC}
              cargarDatosProyecto={cargarDatosProyecto}
              showCustomAlert={showCustomAlert}
              esCliente={datosCliente?.esCliente}
            />
          )}

          {/* Administración - Honorarios */}
          {(proyectoSeleccionado?.tipo_obra === "administracion" || proyectoSeleccionado?.tipo_obra === "administracion_porcentaje") && (
            <TarjetaConcepto
              titulo="Honorarios"
              datos={datosHonorarios}
              cotizacionDolar={cotizacionDolar}
              isMobile={isMobile}
              tipo="honorarios"
              formatCurrency={formatCurrency}
              obtenerNombreMetodo={obtenerNombreMetodo}
              convertirMonto={convertirMonto}
              esMetodoPersonalizado={esMetodoPersonalizado}
              proyectoSeleccionado={proyectoSeleccionado}
              indicesCAC={indicesCAC}
              cargarDatosProyecto={cargarDatosProyecto}
              showCustomAlert={showCustomAlert}
              esCliente={datosCliente?.esCliente}
            />
          )}

          {/* Administración - Mano de Obra */}
          {(proyectoSeleccionado?.tipo_obra === "administracion" || proyectoSeleccionado?.tipo_obra === "administracion_porcentaje") &&
            proyectoSeleccionado?.mano_obra_separada && (
              <TarjetaConcepto
                titulo="Mano de Obra"
                datos={datosManoObra}
                cotizacionDolar={cotizacionDolar}
                isMobile={isMobile}
                tipo="mano_obra"
                formatCurrency={formatCurrency}
                obtenerNombreMetodo={obtenerNombreMetodo}
                convertirMonto={convertirMonto}
                esMetodoPersonalizado={esMetodoPersonalizado}
                proyectoSeleccionado={proyectoSeleccionado}
                indicesCAC={indicesCAC}
                cargarDatosProyecto={cargarDatosProyecto}
                showCustomAlert={showCustomAlert}
                esCliente={datosCliente?.esCliente}
              />
            )}

          {/* Administración - Honorarios por Adicionales */}
          {(proyectoSeleccionado?.tipo_obra === "administracion" || proyectoSeleccionado?.tipo_obra === "administracion_porcentaje") &&
            proyectoSeleccionado?.adicionales_honorarios_habilitado && datosAdicionales && (
              <TarjetaConcepto
                titulo="Honorarios por Adicionales"
                datos={datosAdicionales}
                cotizacionDolar={cotizacionDolar}
                isMobile={isMobile}
                tipo="adicionales"
                formatCurrency={formatCurrency}
                obtenerNombreMetodo={obtenerNombreMetodo}
                convertirMonto={convertirMonto}
                esMetodoPersonalizado={esMetodoPersonalizado}
                proyectoSeleccionado={proyectoSeleccionado}
                indicesCAC={indicesCAC}
                cargarDatosProyecto={cargarDatosProyecto}
                showCustomAlert={showCustomAlert}
                esCliente={datosCliente?.esCliente}
              />
            )}
        </div>
      )}

      {/* Modal personalizado para alertas */}
      <CustomModal
        isOpen={customModal.isOpen}
        onClose={() => setCustomModal((prev) => ({ ...prev, isOpen: false }))}
        type={customModal.type}
        title={customModal.title}
        message={customModal.message}
        onConfirm={customModal.onConfirm}
        isMobile={isMobile}
      />

      <style jsx>{`
        /* Configuración anti-zoom */
        input[type="text"],
        input[type="number"],
        input[type="date"],
        input[type="email"],
        input[type="tel"],
        input[type="search"],
        textarea,
        select {
          font-size: 16px !important;
          touch-action: manipulation;
          -webkit-appearance: none !important;
          appearance: none !important;
        }

        input:focus,
        textarea:focus,
        select:focus {
          font-size: 16px !important;
          outline: none;
          -webkit-appearance: none !important;
          appearance: none !important;
        }

        @media (max-width: 768px) {
          input,
          textarea,
          select,
          button {
            font-size: 16px !important;
            -webkit-appearance: none !important;
            appearance: none !important;
          }
        }

        /* Animaciones estándar */
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        .loading-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        @keyframes pulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        /* Hover effects */
        .button-hover:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
        }

        .action-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
      `}</style>
    </div>
  );
}

// TARJETA PRINCIPAL MEJORADA - CON CONTROL DE CLIENTES
function TarjetaConcepto({
  titulo,
  datos,
  cotizacionDolar,
  isMobile,
  tipo,
  formatCurrency,
  obtenerNombreMetodo,
  convertirMonto,
  esMetodoPersonalizado,
  proyectoSeleccionado,
  indicesCAC,
  cargarDatosProyecto,
  showCustomAlert,
  esCliente,
}) {
  const [tabActiva, setTabActiva] = useState("resumen");

  // DETERMINAR TABS DISPONIBLES SEGÚN TIPO DE USUARIO
  const getTabsDisponibles = () => {
    if (!datos) return ["resumen"]; // Valor por defecto si no hay datos

    if (esCliente) {
      // CLIENTES: Solo resumen y pagos (no configuración)
      return datos.seActualiza
        ? ["resumen", "evolucion", "pagos"]
        : ["resumen", "pagos"];
    } else {
      // EMPLEADOS: Todas las tabs
      return datos.seActualiza
        ? ["resumen", "evolucion", "pagos", "configuracion"]
        : ["resumen", "pagos", "configuracion"];
    }
  };

  const tabsDisponibles = getTabsDisponibles();

  // ASEGURAR QUE LA TAB ACTIVA SEA VÁLIDA
  useEffect(() => {
    if (!tabsDisponibles.includes(tabActiva)) {
      setTabActiva("resumen");
    }
  }, [tabsDisponibles, tabActiva]);

  if (!datos) return null;

  if (datos.error) {
    return (
      <div
        style={{
          background: "white",
          borderRadius: "16px",
          padding: isMobile ? "20px" : "25px",
          boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
          border: "2px solid #fecaca",
          animation: "slideInRight 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            marginBottom: "12px",
          }}
        >
          <div
            style={{
              width: "32px",
              height: "32px",
              background: "#fef2f2",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <XCircle size={16} color="#dc2626" />
          </div>
          <h3
            style={{
              fontSize: isMobile ? "16px" : "18px",
              fontWeight: "600",
              color: "#dc2626",
              margin: 0,
            }}
          >
            {titulo}
          </h3>
        </div>
        <p style={{ color: "#ef4444", fontSize: "14px", margin: 0 }}>
          {datos.error}
        </p>
      </div>
    );
  }

  const getColorTema = () => {
    switch (tipo) {
      case "llave_en_mano":
        return { primary: "#f59e0b", secondary: "#fef3cd", accent: "#92400e" };
      case "honorarios":
        return { primary: "#2563eb", secondary: "#e0e7ff", accent: "#2563eb" };
      case "mano_obra":
        return { primary: "#06b6d4", secondary: "#e0f7fa", accent: "#0891b2" };
      case "adicionales":
        return { primary: "#8b5cf6", secondary: "#f3e8ff", accent: "#7c3aed" };
      default:
        return { primary: "#64748b", secondary: "#f8fafc", accent: "#475569" };
    }
  };

  const colores = getColorTema();
  const resumen = datos.resumenFinal;

  return (
    <div
      style={{
        background: "white",
        borderRadius: "16px",
        boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
        border: "1px solid #f1f5f9",
        overflow: "hidden",
        animation: "slideInRight 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {/* Header de la tarjeta MEJORADO */}
      <div
        style={{
          background: colores.primary,
          color: "white",
          padding: isMobile ? "20px" : "24px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "12px",
            marginBottom: "16px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <div
              style={{
                width: "24px",
                height: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {tipo === "llave_en_mano" && <Building2 size={20} />}
              {tipo === "honorarios" && <User size={20} color="white" />}
              {tipo === "mano_obra" && <Users size={20} />}
              {tipo === "adicionales" && <Plus size={20} color="white" />}
            </div>
            <h3
              style={{
                fontSize: isMobile ? "16px" : "18px",
                fontWeight: "600",
                margin: 0,
              }}
            >
              {titulo}
            </h3>
          </div>

          {datos.seActualiza && (
            <div
              style={{
                background: "rgba(255,255,255,0.2)",
                color: "white",
                padding: "4px 10px",
                borderRadius: "12px",
                fontSize: "11px",
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <BarChart3 size={12} />
              {obtenerNombreMetodo(datos.metodoActualizacion)}
            </div>
          )}
        </div>

        {/* Métricas principales MEJORADAS */}
        {datos.seActualiza ? (
          // CON ACTUALIZACIÓN: Mostrar monto en pesos + unidades
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
              gap: "12px",
            }}
          >
            <div
              style={{
                background: "rgba(255,255,255,0.15)",
                padding: "12px",
                borderRadius: "8px",
                textAlign: "center",
              }}
            >
              <p
                style={{
                  fontSize: "10px",
                  margin: "0 0 4px 0",
                  fontWeight: "600",
                  opacity: 0.9,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                {datos.esPorcentaje ? "Presupuesto según gastos" : "Presupuesto inicial"}
              </p>
              <p
                style={{
                  fontSize: isMobile ? "14px" : "16px",
                  fontWeight: "700",
                  margin: 0,
                }}
              >
                {formatCurrency(datos.montoInicial, datos.monedaInicial)}
              </p>
              <p style={{ fontSize: "9px", margin: 0, opacity: 0.8 }}>
                {datos.fechaInicioActualizacion ? new Date(datos.fechaInicioActualizacion).toLocaleDateString('es-AR', { month: 'short', year: 'numeric' }) : ''} • {(resumen.totalUnidades ?? 0).toFixed(2)} pts
              </p>
            </div>

            <div
              style={{
                background: "rgba(255,255,255,0.15)",
                padding: "12px",
                borderRadius: "8px",
                textAlign: "center",
              }}
            >
              <p
                style={{
                  fontSize: "10px",
                  margin: "0 0 4px 0",
                  fontWeight: "600",
                  opacity: 0.9,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Pagado
              </p>
              <p
                style={{
                  fontSize: isMobile ? "14px" : "16px",
                  fontWeight: "700",
                  margin: 0,
                }}
              >
                {formatCurrency(resumen.pagosAcumulados, datos.monedaInicial)}
              </p>
              <p style={{ fontSize: "9px", margin: 0, opacity: 0.8 }}>
                {(resumen.unidadesGastadas ?? 0).toFixed(2)} pts
              </p>
            </div>

            <div
              style={{
                background: "rgba(255,255,255,0.15)",
                padding: "12px",
                borderRadius: "8px",
                textAlign: "center",
              }}
            >
              <p
                style={{
                  fontSize: "10px",
                  margin: "0 0 4px 0",
                  fontWeight: "600",
                  opacity: 0.9,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Saldo actualizado
              </p>
              <p
                style={{
                  fontSize: isMobile ? "14px" : "16px",
                  fontWeight: "700",
                  margin: 0,
                  color: resumen.saldoPendiente > 0 ? "white" : "#86efac",
                }}
              >
                {formatCurrency(resumen.saldoPendiente, datos.monedaInicial)}
              </p>
              <p style={{ fontSize: "9px", margin: 0, opacity: 0.8 }}>
                Índice: {Math.round(resumen.ultimoIndiceUsado) || "N/A"}
              </p>
            </div>
          </div>
        ) : (
          // SIN ACTUALIZACIÓN: Mostrar datos simples
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
              gap: "12px",
            }}
          >
            <div
              style={{
                background: "rgba(255,255,255,0.15)",
                padding: "12px",
                borderRadius: "8px",
                textAlign: "center",
              }}
            >
              <p
                style={{
                  fontSize: "10px",
                  margin: "0 0 6px 0",
                  fontWeight: "600",
                  opacity: 0.9,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Presupuesto
              </p>
              <p
                style={{
                  fontSize: isMobile ? "16px" : "18px",
                  fontWeight: "700",
                  margin: 0,
                }}
              >
                {formatCurrency(datos.montoInicial, datos.monedaInicial)}
              </p>
              {datos.detalleCalculo && (
                <p style={{ fontSize: "10px", color: "white", margin: 0 }}>{datos.detalleCalculo}</p>
              )}
            </div>

            <div
              style={{
                background: "rgba(255,255,255,0.15)",
                padding: "12px",
                borderRadius: "8px",
                textAlign: "center",
              }}
            >
              <p
                style={{
                  fontSize: "10px",
                  margin: "0 0 6px 0",
                  fontWeight: "600",
                  opacity: 0.9,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Pagado
              </p>
              <p
                style={{
                  fontSize: isMobile ? "16px" : "18px",
                  fontWeight: "700",
                  margin: 0,
                }}
              >
                {formatCurrency(resumen.pagosAcumulados, datos.monedaInicial)}
              </p>
            </div>

            <div
              style={{
                background: "rgba(255,255,255,0.15)",
                padding: "12px",
                borderRadius: "8px",
                textAlign: "center",
              }}
            >
              <p
                style={{
                  fontSize: "10px",
                  margin: "0 0 6px 0",
                  fontWeight: "600",
                  opacity: 0.9,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Saldo
              </p>
              <p
                style={{
                  fontSize: isMobile ? "16px" : "18px",
                  fontWeight: "700",
                  margin: 0,
                  color: resumen.saldoPendiente > 0 ? "white" : "#86efac",
                }}
              >
                {formatCurrency(resumen.saldoPendiente, datos.monedaInicial)}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Navegación por tabs - FILTRADA PARA CLIENTES */}
      <div
        style={{
          background: "#f8fafc",
          borderBottom: "1px solid #e2e8f0",
          padding: "0",
        }}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0",
          }}
        >
          {tabsDisponibles.map((tab) => (
            <button
              key={tab}
              onClick={() => setTabActiva(tab)}
              style={{
                flex: isMobile ? "1" : "none",
                padding: isMobile ? "12px 8px" : "14px 20px",
                background: tabActiva === tab ? "white" : "transparent",
                border: "none",
                borderBottom:
                  tabActiva === tab
                    ? `3px solid ${colores.primary}`
                    : "3px solid transparent",
                fontSize: isMobile ? "12px" : "14px",
                fontWeight: tabActiva === tab ? "600" : "500",
                color: tabActiva === tab ? colores.primary : "#64748b",
                cursor: "pointer",
                transition: "all 0.2s ease",
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
              }}
            >
              {tab === "resumen" && <BarChart3 size={14} />}
              {tab === "evolucion" && <TrendingUp size={14} />}
              {tab === "pagos" && <DollarSign size={14} />}
              {tab === "configuracion" && <Settings size={14} />}
              {isMobile ? (
                ""
              ) : (
                <>
                  {tab === "resumen" && "Resumen"}
                  {tab === "evolucion" && "Evolución"}
                  {tab === "pagos" && "Pagos"}
                  {tab === "configuracion" && "Configuración"}
                </>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Contenido de las tabs */}
      <div style={{ padding: isMobile ? "20px" : "24px" }}>
        {tabActiva === "resumen" && (
          <TabResumen
            datos={datos}
            formatCurrency={formatCurrency}
            isMobile={isMobile}
            colores={colores}
            obtenerNombreMetodo={obtenerNombreMetodo}
          />
        )}

        {tabActiva === "evolucion" && datos.seActualiza && (
          <TabEvolucion
            datos={datos}
            formatCurrency={formatCurrency}
            isMobile={isMobile}
            colores={colores}
            obtenerNombreMetodo={obtenerNombreMetodo}
          />
        )}

        {tabActiva === "pagos" && (
          <TabPagos
            datos={datos}
            formatCurrency={formatCurrency}
            isMobile={isMobile}
            colores={colores}
            convertirMonto={convertirMonto}
          />
        )}

        {tabActiva === "configuracion" && !esCliente && (
          <TabConfiguracion
            datos={datos}
            formatCurrency={formatCurrency}
            isMobile={isMobile}
            colores={colores}
            obtenerNombreMetodo={obtenerNombreMetodo}
            esMetodoPersonalizado={esMetodoPersonalizado}
            proyectoSeleccionado={proyectoSeleccionado}
            indicesCAC={indicesCAC}
            tipo={tipo}
            cargarDatosProyecto={cargarDatosProyecto}
            showCustomAlert={showCustomAlert}
          />
        )}

        {/* MENSAJE SI EL CLIENTE INTENTA VER CONFIGURACIÓN */}
        {tabActiva === "configuracion" && esCliente && (
          <div
            style={{
              background: "#fef3cd",
              border: "1px solid #f59e0b",
              borderRadius: "12px",
              padding: "20px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: "48px",
                height: "48px",
                background: "rgba(245, 158, 11, 0.2)",
                borderRadius: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
              }}
            >
              <Settings size={24} color="#f59e0b" />
            </div>
            <h4
              style={{
                color: "#92400e",
                marginBottom: "8px",
                fontSize: "16px",
                fontWeight: "600",
              }}
            >
              Sección No Disponible
            </h4>
            <p
              style={{
                color: "#92400e",
                fontSize: "14px",
                margin: 0,
                lineHeight: "1.5",
              }}
            >
              La configuración del sistema de actualización solo está disponible
              para el personal de la constructora.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// TAB RESUMEN
function TabResumen({
  datos,
  formatCurrency,
  isMobile,
  colores,
  obtenerNombreMetodo,
}) {
  const resumen = datos.resumenFinal;

  if (!datos.seActualiza) {
    // RESUMEN SIMPLE SIN ACTUALIZACIÓN
    return (
      <div style={{ display: "grid", gap: "16px" }}>
        <div
          style={{
            background: "#f8fafc",
            padding: "16px",
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "12px",
            }}
          >
            <BarChart3 size={16} color={colores.primary} />
            <h4
              style={{
                margin: 0,
                fontSize: "16px",
                fontWeight: "600",
                color: "#1e293b",
              }}
            >
              Resumen Simple
            </h4>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
              gap: "16px",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <p
                style={{
                  fontSize: "11px",
                  color: "#64748b",
                  fontWeight: "600",
                  marginBottom: "6px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Presupuesto Inicial
              </p>
              <p
                style={{
                  fontSize: "18px",
                  fontWeight: "700",
                  color: "#1e293b",
                  margin: 0,
                }}
              >
                {formatCurrency(datos.montoInicial, datos.monedaInicial)}
              </p>
            </div>
            <div style={{ textAlign: "center" }}>
              <p
                style={{
                  fontSize: "11px",
                  color: "#64748b",
                  fontWeight: "600",
                  marginBottom: "6px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Total Pagado
              </p>
              <p
                style={{
                  fontSize: "18px",
                  fontWeight: "700",
                  color: "#059669",
                  margin: 0,
                }}
              >
                {formatCurrency(resumen.pagosAcumulados, datos.monedaInicial)}
              </p>
            </div>
            <div style={{ textAlign: "center" }}>
              <p
                style={{
                  fontSize: "11px",
                  color: "#64748b",
                  fontWeight: "600",
                  marginBottom: "6px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Saldo Pendiente
              </p>
              <p
                style={{
                  fontSize: "18px",
                  fontWeight: "700",
                  color: resumen.saldoPendiente > 0 ? "#dc2626" : "#059669",
                  margin: 0,
                }}
              >
                {formatCurrency(resumen.saldoPendiente, datos.monedaInicial)}
              </p>
            </div>
          </div>
        </div>

        <div
          style={{
            background: "#eff6ff",
            border: "1px solid #bfdbfe",
            borderRadius: "12px",
            padding: "16px",
          }}
        >
          <div
            style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}
          >
            <Info size={20} color="#1e40af" />
            <div>
              <h5
                style={{
                  margin: "0 0 6px 0",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#1e40af",
                }}
              >
                Sin sistema de actualización
              </h5>
              <p
                style={{
                  margin: 0,
                  fontSize: "13px",
                  color: "#1e40af",
                  lineHeight: "1.5",
                }}
              >
                Este concepto mantiene su valor original. El saldo se calcula
                restando los pagos del presupuesto inicial, sin aplicar
                actualizaciones por inflación.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // RESUMEN CON SISTEMA DE ACTUALIZACIÓN
  const porcentajePagado =
    resumen.totalUnidades > 0
      ? (resumen.unidadesGastadas / resumen.totalUnidades) * 100
      : 0;

  return (
    <div style={{ display: "grid", gap: "16px" }}>
      {/* Progreso en unidades */}
      <div
        style={{
          background: "#f8fafc",
          padding: "16px",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "10px",
          }}
        >
          <h4
            style={{
              margin: 0,
              fontSize: "14px",
              fontWeight: "600",
              color: "#1e293b",
            }}
          >
            Progreso en Unidades del Sistema
          </h4>
          <span
            style={{
              fontSize: "13px",
              fontWeight: "600",
              color: colores.primary,
            }}
          >
            {porcentajePagado.toFixed(1)}%
          </span>
        </div>

        <div
          style={{
            width: "100%",
            height: "8px",
            background: "#e2e8f0",
            borderRadius: "4px",
            overflow: "hidden",
            marginBottom: "6px",
          }}
        >
          <div
            style={{
              width: `${Math.min(porcentajePagado, 100)}%`,
              height: "100%",
              background: colores.primary,
              transition: "width 0.3s ease",
            }}
          ></div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: "11px",
            color: "#64748b",
          }}
        >
          <span>Pagadas: {Math.round(resumen.unidadesGastadas)} pts</span>
          <span>Total: {Math.round(resumen.totalUnidades)} pts</span>
        </div>
      </div>

      {/* Información del sistema */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
          gap: "12px",
        }}
      >
        <div
          style={{
            padding: "16px",
            background: "#f8fafc",
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
          }}
        >
          <h5
            style={{
              margin: "0 0 10px 0",
              fontSize: "13px",
              fontWeight: "600",
              color: "#64748b",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <BarChart3 size={14} />
            Unidades del Sistema
          </h5>
          <div style={{ display: "grid", gap: "6px", fontSize: "13px" }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "#64748b" }}>Total presupuesto:</span>
              <span style={{ fontWeight: "600" }}>
                {(resumen.totalUnidades ?? 0).toFixed(2)}
              </span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "#64748b" }}>Pagadas:</span>
              <span style={{ fontWeight: "600", color: "#dc2626" }}>
                {(resumen.unidadesGastadas ?? 0).toFixed(2)}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                paddingTop: "6px",
                borderTop: "1px solid #e2e8f0",
              }}
            >
              <span style={{ color: "#1e293b", fontWeight: "600" }}>
                Saldo (unidades):
              </span>
              <span
                style={{
                  fontWeight: "700",
                  color: resumen.unidades > 0 ? "#059669" : "#dc2626",
                }}
              >
                {(resumen.unidades ?? 0).toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        <div
          style={{
            padding: "16px",
            background: "#f8fafc",
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
          }}
        >
          <h5
            style={{
              margin: "0 0 10px 0",
              fontSize: "13px",
              fontWeight: "600",
              color: "#64748b",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <DollarSign size={14} />
            Valores en Pesos
          </h5>
          <div style={{ display: "grid", gap: "6px", fontSize: "13px" }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "#64748b" }}>Último índice:</span>
              <span style={{ fontWeight: "600" }}>
                {Math.round(resumen.ultimoIndiceUsado) || "N/A"}
              </span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "#64748b" }}>Total pagado:</span>
              <span style={{ fontWeight: "600", color: "#059669" }}>
                {formatCurrency(resumen.pagosAcumulados, datos.monedaInicial)}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                paddingTop: "6px",
                borderTop: "1px solid #e2e8f0",
              }}
            >
              <span style={{ color: "#1e293b", fontWeight: "600" }}>
                Saldo actual:
              </span>
              <span
                style={{
                  fontWeight: "700",
                  color: resumen.saldoPendiente > 0 ? "#dc2626" : "#059669",
                }}
              >
                {formatCurrency(resumen.saldoPendiente, datos.monedaInicial)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Información sobre el sistema */}
      <div
        style={{
          background: "#eff6ff",
          border: "1px solid #bfdbfe",
          borderRadius: "12px",
          padding: "16px",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
          <BarChart3 size={20} color="#1e40af" />
          <div>
            <h5
              style={{
                margin: "0 0 6px 0",
                fontSize: "14px",
                fontWeight: "600",
                color: "#1e40af",
              }}
            >
              Sistema de Actualización:{" "}
              {obtenerNombreMetodo(datos.metodoActualizacion)}
            </h5>
            <p
              style={{
                margin: 0,
                fontSize: "13px",
                color: "#1e40af",
                lineHeight: "1.5",
              }}
            >
              El presupuesto se convierte a "unidades" usando el índice del mes
              de inicio. Los pagos se descuentan en unidades según el índice de
              cada mes. El saldo se muestra en pesos usando el último índice
              disponible.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// TAB EVOLUCIÓN MEJORADO
function TabEvolucion({
  datos,
  formatCurrency,
  isMobile,
  colores,
  obtenerNombreMetodo,
}) {
  if (!datos.evolucion || datos.evolucion.length === 0) {
    return (
      <div
        style={{ textAlign: "center", padding: "40px 20px", color: "#64748b" }}
      >
        <div
          style={{
            width: "48px",
            height: "48px",
            background: "#f8fafc",
            borderRadius: "24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 16px",
          }}
        >
          <TrendingUp size={24} color="#64748b" />
        </div>
        <p style={{ fontSize: "14px", margin: 0 }}>
          No hay datos de evolución disponibles
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: "16px" }}>
      {/* Explicación del sistema */}
      <div
        style={{
          background: "#f0f9ff",
          border: "1px solid #0ea5e9",
          borderRadius: "12px",
          padding: "16px",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
          <TrendingUp size={20} color="#0369a1" />
          <div>
            <h4
              style={{
                margin: "0 0 6px 0",
                fontSize: "14px",
                fontWeight: "600",
                color: "#0369a1",
              }}
            >
              Evolución del Sistema:{" "}
              {obtenerNombreMetodo(datos.metodoActualizacion)}
            </h4>
            <p
              style={{
                margin: 0,
                fontSize: "13px",
                color: "#0369a1",
                lineHeight: "1.5",
              }}
            >
              Cada mes se aplica el índice oficial o el último disponible
              (provisorio). Cuando se publique el índice real, los cálculos se
              actualizarán automáticamente.
            </p>
          </div>
        </div>
      </div>

      {/* Tabla de evolución MEJORADA */}
      <div
        style={{
          background: "white",
          borderRadius: "12px",
          overflow: "hidden",
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        }}
      >
        <div
          style={{
            background: colores.primary,
            color: "white",
            padding: "12px 16px",
            fontSize: "14px",
            fontWeight: "600",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <TrendingUp size={16} />
          Evolución Mensual en Unidades
        </div>

        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: isMobile ? "11px" : "12px",
            }}
          >
            <thead>
              <tr style={{ background: "#f8fafc" }}>
                <th
                  style={{
                    padding: "8px 6px",
                    textAlign: "left",
                    fontWeight: "600",
                    color: "#475569",
                    fontSize: "11px",
                  }}
                >
                  Mes
                </th>
                <th
                  style={{
                    padding: "8px 6px",
                    textAlign: "center",
                    fontWeight: "600",
                    color: "#475569",
                    fontSize: "11px",
                  }}
                >
                  Índice
                </th>
                <th
                  style={{
                    padding: "8px 6px",
                    textAlign: "center",
                    fontWeight: "600",
                    color: "#475569",
                    fontSize: "11px",
                  }}
                >
                  Estado
                </th>
                {datos.esPorcentaje && (
                  <th
                    style={{
                      padding: "8px 6px",
                      textAlign: "right",
                      fontWeight: "600",
                      color: "#475569",
                      fontSize: "11px",
                    }}
                  >
                    Gastos del Mes
                  </th>
                )}
                <th
                  style={{
                    padding: "8px 6px",
                    textAlign: "right",
                    fontWeight: "600",
                    color: "#475569",
                    fontSize: "11px",
                  }}
                >
                  Pago Honorarios
                </th>
                <th
                  style={{
                    padding: "8px 6px",
                    textAlign: "right",
                    fontWeight: "600",
                    color: "#475569",
                    fontSize: "11px",
                  }}
                >
                  Pago Hon. (unidades)
                </th>
                <th
                  style={{
                    padding: "8px 6px",
                    textAlign: "right",
                    fontWeight: "600",
                    color: "#475569",
                    fontSize: "11px",
                  }}
                >
                  Saldo (Unidades)
                </th>
                <th
                  style={{
                    padding: "8px 6px",
                    textAlign: "right",
                    fontWeight: "600",
                    color: "#475569",
                    fontSize: "11px",
                  }}
                >
                  Saldo (Pesos)
                </th>
                <th
                  style={{
                    padding: "8px 6px",
                    textAlign: "center",
                    fontWeight: "600",
                    color: "#475569",
                    fontSize: "11px",
                  }}
                >
                  Cant.
                </th>
              </tr>
            </thead>
            <tbody>
              {datos.evolucion.map((periodo, index) => (
                <tr
                  key={index}
                  style={{
                    borderBottom: "1px solid #f1f5f9",
                    background: index % 2 === 0 ? "white" : "#f8fafc",
                  }}
                >
                  <td
                    style={{
                      padding: "8px 6px",
                      fontWeight: "600",
                      fontSize: "11px",
                    }}
                  >
                    {periodo.mes}
                  </td>
                  <td
                    style={{
                      padding: "8px 6px",
                      textAlign: "center",
                      fontSize: "10px",
                    }}
                  >
                    {periodo.indiceUsado ? Math.round(periodo.indiceUsado) : "-"}
                  </td>
                  <td style={{ padding: "8px 6px", textAlign: "center" }}>
                    {periodo.esEditado ? (
                      <span
                        style={{
                          background: "#e0e7ff",
                          color: "#4338ca",
                          padding: "2px 4px",
                          borderRadius: "4px",
                          fontSize: "8px",
                          fontWeight: "600",
                        }}
                      >
                        EDIT
                      </span>
                    ) : periodo.esProvisorio ? (
                      <span
                        style={{
                          background: "#fef3cd",
                          color: "#92400e",
                          padding: "2px 4px",
                          borderRadius: "4px",
                          fontSize: "8px",
                          fontWeight: "600",
                        }}
                      >
                        ESP
                      </span>
                    ) : (
                      <span
                        style={{
                          background: "#dcfce7",
                          color: "#166534",
                          padding: "2px 4px",
                          borderRadius: "4px",
                          fontSize: "8px",
                          fontWeight: "600",
                        }}
                      >
                        OFIC
                      </span>
                    )}
                  </td>
                  {datos.esPorcentaje && (
                    <td
                      style={{
                        padding: "8px 6px",
                        textAlign: "right",
                        color: periodo.cantidadGastos > 0 ? "#6366f1" : "#64748b",
                        fontSize: "10px",
                      }}
                    >
                      {periodo.cantidadGastos > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "1px" }}>
                          <span style={{ fontWeight: "600" }}>
                            {formatCurrency(periodo.totalGastoBrutoMes, datos.monedaInicial)}
                          </span>
                          <span style={{ fontSize: "9px", color: "#94a3b8" }}>
                            × {periodo.porcentajeGastos}% = {formatCurrency(periodo.totalGastadoMes, datos.monedaInicial)}
                          </span>
                          <span style={{ fontSize: "9px", color: "#94a3b8" }}>
                            {Math.round(periodo.unidadesGastosMes)} pts
                          </span>
                        </div>
                      ) : "-"}
                    </td>
                  )}
                  <td
                    style={{
                      padding: "8px 6px",
                      textAlign: "right",
                      color:
                        periodo.pagosDelMes?.length > 0 ? "#059669" : "#64748b",
                      fontSize: "11px",
                    }}
                  >
                    {periodo.pagosDelMes?.length > 0
                      ? formatCurrency(
                          periodo.pagosDelMes.reduce(
                            (sum, p) => sum + p.montoConvertido,
                            0
                          ),
                          datos.monedaInicial
                        )
                      : "-"}
                  </td>
                  <td
                    style={{
                      padding: "8px 6px",
                      textAlign: "right",
                      color: periodo.unidadesDelMes > 0 ? "#dc2626" : "#64748b",
                      fontSize: "11px",
                    }}
                  >
                    {periodo.unidadesDelMes > 0
                      ? (periodo.unidadesDelMes).toFixed(2)
                      : "-"}
                  </td>
                  <td
                    style={{
                      padding: "8px 6px",
                      textAlign: "right",
                      fontWeight: "600",
                      color:
                        periodo.unidadesRestantes > 0 ? "#059669" : "#dc2626",
                      fontSize: "11px",
                    }}
                  >
                    {(periodo.unidadesRestantes ?? 0).toFixed(2)}
                  </td>
                  <td
                    style={{
                      padding: "8px 6px",
                      textAlign: "right",
                      fontWeight: "600",
                      color: periodo.saldoEnPesos > 0 ? "#059669" : "#dc2626",
                      fontSize: "11px",
                    }}
                  >
                    {formatCurrency(periodo.saldoEnPesos, datos.monedaInicial)}
                  </td>
                  <td style={{ padding: "8px 6px", textAlign: "center" }}>
                    {periodo.cantidadPagos > 0 ? (
                      <span
                        style={{
                          background: colores.light,
                          color: colores.primary,
                          padding: "2px 6px",
                          borderRadius: "8px",
                          fontSize: "9px",
                          fontWeight: "600",
                        }}
                      >
                        {periodo.cantidadPagos}
                      </span>
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// TAB PAGOS
function TabPagos({
  datos,
  formatCurrency,
  isMobile,
  colores,
  convertirMonto,
}) {
  if (!datos.pagos || datos.pagos.length === 0) {
    return (
      <div
        style={{ textAlign: "center", padding: "40px 20px", color: "#64748b" }}
      >
        <div
          style={{
            width: "48px",
            height: "48px",
            background: "#f8fafc",
            borderRadius: "24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 16px",
          }}
        >
          <DollarSign size={24} color="#64748b" />
        </div>
        <h4
          style={{
            margin: "0 0 6px 0",
            color: "#1e293b",
            fontSize: "16px",
            fontWeight: "600",
          }}
        >
          No hay pagos registrados
        </h4>
        <p style={{ margin: 0, fontSize: "14px" }}>
          Los pagos aparecerán aquí cuando sean cargados en el sistema
        </p>
      </div>
    );
  }

  // Agrupar pagos por mes para mejor visualización
  const pagosPorMes = datos.pagos.reduce((acc, pago) => {
    const fecha = new Date(pago.fecha);
    const mesAno = fecha.toLocaleDateString("es-AR", {
      year: "numeric",
      month: "long",
    });

    if (!acc[mesAno]) {
      acc[mesAno] = [];
    }

    acc[mesAno].push({
      ...pago,
      montoConvertido: convertirMonto(
        pago.monto,
        pago.moneda,
        datos.monedaInicial,
        pago.tipo_cambio
      ),
    });

    return acc;
  }, {});

  return (
    <div style={{ display: "grid", gap: "16px" }}>
      {/* Resumen de pagos */}
      <div
        style={{
          background: "#f8fafc",
          padding: "16px",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
        }}
      >
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: "14px",
            fontWeight: "600",
            color: "#1e293b",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <DollarSign size={16} color={colores.primary} />
          Resumen de Pagos
        </h4>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
            gap: "12px",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <p
              style={{
                margin: "0 0 4px 0",
                fontSize: "11px",
                color: "#64748b",
                fontWeight: "600",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Total Pagos
            </p>
            <p
              style={{
                margin: 0,
                fontSize: "16px",
                fontWeight: "700",
                color: colores.primary,
              }}
            >
              {datos.pagos.length}
            </p>
          </div>
          <div style={{ textAlign: "center" }}>
            <p
              style={{
                margin: "0 0 4px 0",
                fontSize: "11px",
                color: "#64748b",
                fontWeight: "600",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Monto Total
            </p>
            <p
              style={{
                margin: 0,
                fontSize: "16px",
                fontWeight: "700",
                color: "#059669",
              }}
            >
              {formatCurrency(
                datos.resumenFinal.pagosAcumulados,
                datos.monedaInicial
              )}
            </p>
          </div>
          <div style={{ textAlign: "center" }}>
            <p
              style={{
                margin: "0 0 4px 0",
                fontSize: "11px",
                color: "#64748b",
                fontWeight: "600",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Último Pago
            </p>
            <p
              style={{
                margin: 0,
                fontSize: "16px",
                fontWeight: "700",
                color: "#1e293b",
              }}
            >
              {datos.pagos.length > 0
                ? new Date(
                    datos.pagos[datos.pagos.length - 1].fecha
                  ).toLocaleDateString("es-AR")
                : "-"}
            </p>
          </div>
        </div>
      </div>

      {/* Lista de pagos por mes */}
      {Object.entries(pagosPorMes)
        .sort(([a], [b]) => new Date(b).getTime() - new Date(a).getTime())
        .map(([mes, pagosDelMes]) => (
          <div
            key={mes}
            style={{
              background: "white",
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
              overflow: "hidden",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            }}
          >
            <div
              style={{
                background: "#f8fafc",
                padding: "12px 16px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "1px solid #e2e8f0",
              }}
            >
              <h5
                style={{
                  margin: 0,
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#1e293b",
                  textTransform: "capitalize",
                }}
              >
                {mes}
              </h5>
              <span
                style={{
                  background: colores.primary,
                  color: "white",
                  padding: "3px 8px",
                  borderRadius: "8px",
                  fontSize: "11px",
                  fontWeight: "600",
                }}
              >
                {pagosDelMes.length} pago{pagosDelMes.length !== 1 ? "s" : ""}
              </span>
            </div>

            <div style={{ padding: "12px" }}>
              {pagosDelMes.map((pago, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "12px",
                    background: index % 2 === 0 ? "#f8fafc" : "white",
                    borderRadius: "8px",
                    marginBottom: index < pagosDelMes.length - 1 ? "6px" : "0",
                    border: "1px solid #f1f5f9",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "6px",
                      }}
                    >
                      <Calendar size={12} color="#64748b" />
                      <span
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "#1e293b",
                        }}
                      >
                        {new Date(pago.fecha).toLocaleDateString("es-AR")}
                      </span>
                      {pago.tiene_comprobante && (
                        <FileText size={10} color="#059669" />
                      )}
                    </div>

                    <p
                      style={{
                        margin: "0 0 4px 0",
                        fontSize: "13px",
                        color: "#64748b",
                      }}
                    >
                      {pago.conceptos_entrada?.nombre ||
                        "Concepto no especificado"}
                    </p>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        flexWrap: "wrap",
                        fontSize: "11px",
                        color: "#64748b",
                      }}
                    >
                      <span>
                        <strong>Original:</strong>{" "}
                        {formatCurrency(pago.monto, pago.moneda)}
                      </span>
                      {pago.moneda !== datos.monedaInicial && (
                        <>
                          <span>•</span>
                          <span>
                            <strong>Convertido:</strong>{" "}
                            {formatCurrency(
                              pago.montoConvertido,
                              datos.monedaInicial
                            )}
                          </span>
                          <span>•</span>
                          <span>
                            <strong>T.C.:</strong>{" "}
                            {(pago.tipo_cambio || 1000).toLocaleString("es-AR")}
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  <div
                    style={{
                      textAlign: "right",
                      marginLeft: "12px",
                    }}
                  >
                    <p
                      style={{
                        margin: "0 0 4px 0",
                        fontSize: isMobile ? "14px" : "16px",
                        fontWeight: "700",
                        color: "#059669",
                      }}
                    >
                      {formatCurrency(
                        pago.montoConvertido,
                        datos.monedaInicial
                      )}
                    </p>

                    {pago.comprobante_url && (
                      <a
                        href={pago.comprobante_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                          fontSize: "11px",
                          color: colores.primary,
                          textDecoration: "none",
                          fontWeight: "600",
                        }}
                      >
                        <FileText size={10} />
                        Ver comprobante
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
    </div>
  );
}

// TAB CONFIGURACIÓN CON GESTIÓN DE VALORES PERSONALIZADOS - SOLO PARA EMPLEADOS
function TabConfiguracion({
  datos,
  formatCurrency,
  isMobile,
  colores,
  obtenerNombreMetodo,
  esMetodoPersonalizado,
  proyectoSeleccionado,
  indicesCAC,
  tipo,
  cargarDatosProyecto,
  showCustomAlert,
}) {
  const [valoresPersonalizados, setValoresPersonalizados] = useState([]);
  const [cargandoPersonalizados, setCargandoPersonalizados] = useState(false);
  const [editandoMes, setEditandoMes] = useState(null);
  const [nuevoValor, setNuevoValor] = useState("");
  const [nuevaFecha, setNuevaFecha] = useState("");

  useEffect(() => {
    if (datos.seActualiza) {
      cargarValoresPersonalizados();
    }
  }, [datos.seActualiza, proyectoSeleccionado?.id, tipo]);

  const cargarValoresPersonalizados = async () => {
    setCargandoPersonalizados(true);
    try {
      const { data, error } = await supabase
        .from("valores_personalizados_actualizacion")
        .select("*")
        .eq("proyecto_id", proyectoSeleccionado.id)
        .eq("tipo", tipo)
        .order("fecha_aplicacion", { ascending: true });

      if (!error) {
        setValoresPersonalizados(data || []);
      }
    } catch (err) {
      console.error("Error cargando valores personalizados:", err);
    }
    setCargandoPersonalizados(false);
  };

  const guardarValorPersonalizado = async (fecha, valor) => {
    try {
      const { error } = await supabase
        .from("valores_personalizados_actualizacion")
        .upsert({
          proyecto_id: proyectoSeleccionado.id,
          tipo: tipo,
          fecha_aplicacion: fecha,
          coeficiente: parseFloat(valor),
          observaciones: "Modificado desde la interfaz",
        });

      if (!error) {
        await cargarValoresPersonalizados();
        await cargarDatosProyecto(); // Recalcular
        setEditandoMes(null);
        setNuevoValor("");
        showCustomAlert(
          "success",
          "Éxito",
          "Valor personalizado guardado correctamente"
        );
      } else {
        showCustomAlert("error", "Error", "Error al guardar: " + error.message);
      }
    } catch (err) {
      console.error("Error guardando valor personalizado:", err);
      showCustomAlert("error", "Error", "Error: " + err.message);
    }
  };

  const eliminarValorPersonalizado = async (fecha) => {
    showCustomAlert(
      "confirm",
      "¿Eliminar valor personalizado?",
      "Esta acción eliminará el valor personalizado y se recalcularán los saldos. ¿Estás seguro?",
      async () => {
        try {
          const { error } = await supabase
            .from("valores_personalizados_actualizacion")
            .delete()
            .eq("proyecto_id", proyectoSeleccionado.id)
            .eq("tipo", tipo)
            .eq("fecha_aplicacion", fecha);

          if (!error) {
            await cargarValoresPersonalizados();
            await cargarDatosProyecto(); // Recalcular
            showCustomAlert(
              "success",
              "Éxito",
              "Valor personalizado eliminado correctamente"
            );
          } else {
            showCustomAlert(
              "error",
              "Error",
              "Error al eliminar: " + error.message
            );
          }
        } catch (err) {
          console.error("Error eliminando valor personalizado:", err);
          showCustomAlert("error", "Error", "Error: " + err.message);
        }
      }
    );
  };

  const generarMesesParaConfigurar = () => {
    // SI ES MÉTODO PERSONALIZADO: Solo mostrar valores configurados manualmente
    if (esMetodoPersonalizado(datos.metodoActualizacion)) {
      return [];
    }

    // SI ES CAC OFICIAL: Generar meses desde fecha de inicio hasta hoy
    if (!datos.fechaInicioActualizacion) {
      // Si no hay fecha de inicio, usar fecha del proyecto o enero 2025
      const fechaProyecto =
        proyectoSeleccionado?.fecha_inicio || proyectoSeleccionado?.created_at;
      if (!fechaProyecto) {
        // Sin fecha de referencia, no generar meses
        return [];
      }
    }

    const fechaInicio = datos.fechaInicioActualizacion
      ? new Date(datos.fechaInicioActualizacion + "T00:00:00")
      : new Date(
          (proyectoSeleccionado?.fecha_inicio ||
            proyectoSeleccionado?.created_at) + "T00:00:00"
        );

    const fechaHoy = new Date();
    const meses = [];

    let mesActual = new Date(
      fechaInicio.getFullYear(),
      fechaInicio.getMonth(),
      1
    );

    while (mesActual <= fechaHoy) {
      const fechaString =
        mesActual.toISOString().split("T")[0].substring(0, 7) + "-01";

      // Buscar valor oficial CAC
      const indiceOficial = indicesCAC.find((i) => i.fecha === fechaString);
      let valorOficial = null;

      if (indiceOficial) {
        switch (datos.metodoActualizacion) {
          case "CAC_GENERAL":
            valorOficial = parseFloat(indiceOficial.cac_general);
            break;
          case "CAC_MATERIALES":
            valorOficial = parseFloat(indiceOficial.cac_materiales);
            break;
          case "CAC_MANO_OBRA":
            valorOficial = parseFloat(indiceOficial.cac_mano_obra);
            break;
        }
      }

      // Buscar valor personalizado (override)
      const valorPersonalizado = valoresPersonalizados.find(
        (v) => v.fecha_aplicacion === fechaString
      );

      meses.push({
        fecha: fechaString,
        mes: mesActual.toLocaleDateString("es-AR", {
          year: "numeric",
          month: "long",
        }),
        valorOficial: valorOficial,
        valorPersonalizado: valorPersonalizado?.coeficiente || null,
        tienePersonalizado: !!valorPersonalizado,
      });

      mesActual.setMonth(mesActual.getMonth() + 1);
    }

    return meses.reverse(); // Más recientes primero
  };

  return (
    <div style={{ display: "grid", gap: "16px" }}>
      {/* Información del sistema */}
      <div
        style={{
          background: "#f8fafc",
          padding: "16px",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
        }}
      >
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: "14px",
            fontWeight: "600",
            color: "#1e293b",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <Settings size={16} color={colores.primary} />
          Configuración del Sistema
        </h4>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
            gap: "12px",
          }}
        >
          <div>
            <label
              style={{
                display: "block",
                fontSize: "11px",
                fontWeight: "600",
                color: "#64748b",
                marginBottom: "4px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Se Actualiza
            </label>
            <p
              style={{
                margin: 0,
                fontSize: "13px",
                fontWeight: "600",
                color: datos.seActualiza ? "#059669" : "#dc2626",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              {datos.seActualiza ? (
                <CheckCircle2 size={14} />
              ) : (
                <XCircle size={14} />
              )}
              {datos.seActualiza ? "Sí" : "No (Valor fijo)"}
            </p>
          </div>

          {datos.seActualiza && (
            <>
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "11px",
                    fontWeight: "600",
                    color: "#64748b",
                    marginBottom: "4px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Sistema de Actualización
                </label>
                <p
                  style={{
                    margin: 0,
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#1e293b",
                  }}
                >
                  {obtenerNombreMetodo(datos.metodoActualizacion)}
                </p>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "11px",
                    fontWeight: "600",
                    color: "#64748b",
                    marginBottom: "4px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Fecha de Inicio
                </label>
                <p
                  style={{
                    margin: 0,
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#1e293b",
                  }}
                >
                  {datos.fechaInicioActualizacion
                    ? new Date(
                        datos.fechaInicioActualizacion
                      ).toLocaleDateString("es-AR")
                    : "No especificada"}
                </p>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "11px",
                    fontWeight: "600",
                    color: "#64748b",
                    marginBottom: "4px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Último Índice Usado
                </label>
                <p
                  style={{
                    margin: 0,
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#1e293b",
                  }}
                >
                  {Math.round(datos.resumenFinal?.ultimoIndiceUsado) || "N/A"}
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Gestión de valores por mes */}
      {datos.seActualiza && (
        <div
          style={{
            background: "#f8fafc",
            padding: "16px",
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "12px",
              flexWrap: "wrap",
              gap: "12px",
            }}
          >
            <h4
              style={{
                margin: 0,
                fontSize: "14px",
                fontWeight: "600",
                color: "#1e293b",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <BarChart3 size={16} color={colores.primary} />
              Valores por Mes - {obtenerNombreMetodo(datos.metodoActualizacion)}
            </h4>

            {/* BOTÓN AGREGAR MES: Solo para métodos personalizados */}
            {esMetodoPersonalizado(datos.metodoActualizacion) && (
              <button
                onClick={() => {
                  setEditandoMes("nuevo");
                  setNuevoValor("");
                }}
                style={{
                  background: colores.primary,
                  color: "white",
                  border: "none",
                  padding: "6px 12px",
                  borderRadius: "8px",
                  fontSize: "12px",
                  fontWeight: "600",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
                className="button-hover"
              >
                <Calendar size={12} />
                Agregar Mes
              </button>
            )}
          </div>

          {cargandoPersonalizados ? (
            <div
              style={{ textAlign: "center", padding: "20px", color: "#64748b" }}
            >
              <Loader2
                size={20}
                style={{ animation: "spin 1s linear infinite" }}
              />
              <p style={{ margin: "8px 0 0 0", fontSize: "13px" }}>
                Cargando valores...
              </p>
            </div>
          ) : (
            <div>
              {/* SI ES MÉTODO PERSONALIZADO */}
              {esMetodoPersonalizado(datos.metodoActualizacion) ? (
                <div>
                  {/* Tabla solo de valores personalizados */}
                  {valoresPersonalizados.length > 0 ? (
                    <div style={{ marginBottom: "16px" }}>
                      <div
                        style={{
                          background: "white",
                          border: "1px solid #e2e8f0",
                          borderRadius: "8px",
                          overflow: "hidden",
                        }}
                      >
                        <div style={{ overflowX: "auto" }}>
                          <table
                            style={{
                              width: "100%",
                              borderCollapse: "collapse",
                              fontSize: "12px",
                            }}
                          >
                            <thead style={{ background: "#f8fafc" }}>
                              <tr>
                                <th
                                  style={{
                                    padding: "10px 12px",
                                    textAlign: "left",
                                    fontWeight: "600",
                                    color: "#475569",
                                    fontSize: "11px",
                                  }}
                                >
                                  Mes/Año
                                </th>
                                <th
                                  style={{
                                    padding: "10px 12px",
                                    textAlign: "center",
                                    fontWeight: "600",
                                    color: "#475569",
                                    fontSize: "11px",
                                  }}
                                >
                                  Valor Configurado
                                </th>
                                <th
                                  style={{
                                    padding: "10px 12px",
                                    textAlign: "center",
                                    fontWeight: "600",
                                    color: "#475569",
                                    fontSize: "11px",
                                  }}
                                >
                                  Acciones
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {valoresPersonalizados
                                .sort(
                                  (a, b) =>
                                    new Date(b.fecha_aplicacion) -
                                    new Date(a.fecha_aplicacion)
                                )
                                .map((valor, index) => (
                                  <tr
                                    key={valor.id}
                                    style={{
                                      borderBottom:
                                        index < valoresPersonalizados.length - 1
                                          ? "1px solid #f1f5f9"
                                          : "none",
                                      background:
                                        index % 2 === 0 ? "white" : "#f8fafc",
                                    }}
                                  >
                                    <td
                                      style={{
                                        padding: "10px 12px",
                                        fontWeight: "600",
                                        textTransform: "capitalize",
                                        fontSize: "12px",
                                      }}
                                    >
                                      {new Date(
                                        valor.fecha_aplicacion + "T00:00:00"
                                      ).toLocaleDateString("es-AR", {
                                        year: "numeric",
                                        month: "long",
                                      })}
                                    </td>
                                    <td
                                      style={{
                                        padding: "10px 12px",
                                        textAlign: "center",
                                      }}
                                    >
                                      {editandoMes ===
                                      valor.fecha_aplicacion ? (
                                        <div
                                          style={{
                                            display: "flex",
                                            gap: "4px",
                                            justifyContent: "center",
                                          }}
                                        >
                                          <input
                                            type="number"
                                            step="0.0001"
                                            value={nuevoValor}
                                            onChange={(e) =>
                                              setNuevoValor(e.target.value)
                                            }
                                            style={{
                                              width: "100px",
                                              padding: "4px 6px",
                                              border: "1px solid #d1d5db",
                                              borderRadius: "4px",
                                              fontSize: "11px",
                                            }}
                                          />
                                          <button
                                            onClick={() =>
                                              guardarValorPersonalizado(
                                                valor.fecha_aplicacion,
                                                nuevoValor
                                              )
                                            }
                                            style={{
                                              background: "#059669",
                                              color: "white",
                                              border: "none",
                                              padding: "4px 8px",
                                              borderRadius: "4px",
                                              fontSize: "10px",
                                              cursor: "pointer",
                                            }}
                                          >
                                            <CheckCircle2 size={10} />
                                          </button>
                                          <button
                                            onClick={() => {
                                              setEditandoMes(null);
                                              setNuevoValor("");
                                            }}
                                            style={{
                                              background: "#dc2626",
                                              color: "white",
                                              border: "none",
                                              padding: "4px 8px",
                                              borderRadius: "4px",
                                              fontSize: "10px",
                                              cursor: "pointer",
                                            }}
                                          >
                                            <X size={10} />
                                          </button>
                                        </div>
                                      ) : (
                                        <span
                                          style={{
                                            color: colores.primary,
                                            fontWeight: "600",
                                            fontSize: "13px",
                                          }}
                                        >
                                          {parseFloat(
                                            valor.coeficiente
                                          ).toFixed(2)}
                                        </span>
                                      )}
                                    </td>
                                    <td
                                      style={{
                                        padding: "10px 12px",
                                        textAlign: "center",
                                      }}
                                    >
                                      {editandoMes ===
                                      valor.fecha_aplicacion ? null : (
                                        <div
                                          style={{
                                            display: "flex",
                                            gap: "4px",
                                            justifyContent: "center",
                                          }}
                                        >
                                          <button
                                            onClick={() => {
                                              setEditandoMes(
                                                valor.fecha_aplicacion
                                              );
                                              setNuevoValor(
                                                valor.coeficiente.toString()
                                              );
                                            }}
                                            style={{
                                              background: "#eff6ff",
                                              border: "1px solid #3b82f6",
                                              borderRadius: "4px",
                                              padding: "4px 8px",
                                              color: "#3b82f6",
                                              fontSize: "10px",
                                              cursor: "pointer",
                                              fontWeight: "500",
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "2px",
                                            }}
                                          >
                                            <Edit size={8} />
                                            Editar
                                          </button>
                                          <button
                                            onClick={() =>
                                              eliminarValorPersonalizado(
                                                valor.fecha_aplicacion
                                              )
                                            }
                                            style={{
                                              background: "#fef2f2",
                                              border: "1px solid #ef4444",
                                              borderRadius: "4px",
                                              padding: "4px 8px",
                                              color: "#ef4444",
                                              fontSize: "10px",
                                              cursor: "pointer",
                                              fontWeight: "500",
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "2px",
                                            }}
                                          >
                                            <Trash2 size={8} />
                                            Eliminar
                                          </button>
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div
                      style={{
                        background: "#f0f9ff",
                        border: "2px dashed #0ea5e9",
                        borderRadius: "12px",
                        padding: "24px",
                        textAlign: "center",
                        marginBottom: "16px",
                      }}
                    >
                      <div
                        style={{
                          width: "48px",
                          height: "48px",
                          background: "rgba(14, 165, 233, 0.1)",
                          borderRadius: "24px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          margin: "0 auto 12px",
                        }}
                      >
                        <Calendar size={24} color="#0ea5e9" />
                      </div>
                      <h4
                        style={{
                          color: "#0c4a6e",
                          marginBottom: "8px",
                          fontSize: "14px",
                          fontWeight: "600",
                        }}
                      >
                        No hay meses configurados
                      </h4>
                      <p
                        style={{
                          color: "#0369a1",
                          fontSize: "13px",
                          marginBottom: "16px",
                          lineHeight: "1.4",
                        }}
                      >
                        Agrega los meses que necesites para tu sistema "
                        {datos.metodoActualizacion}"
                      </p>
                      <button
                        onClick={() => {
                          setEditandoMes("nuevo");
                          setNuevoValor("");
                        }}
                        style={{
                          background:
                            "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
                          color: "white",
                          border: "none",
                          padding: "10px 20px",
                          borderRadius: "8px",
                          fontSize: "13px",
                          fontWeight: "600",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          margin: "0 auto",
                        }}
                        className="button-hover"
                      >
                        <Calendar size={14} />
                        Agregar Primer Mes
                      </button>
                    </div>
                  )}

                  {/* Modal inline para agregar nuevo mes - SOLO PERSONALIZADOS */}
                  {editandoMes === "nuevo" && (
                    <div
                      style={{
                        background: "white",
                        border: "2px solid #0ea5e9",
                        borderRadius: "12px",
                        padding: "16px",
                        marginTop: "12px",
                      }}
                    >
                      <h5
                        style={{
                          margin: "0 0 12px 0",
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "#0c4a6e",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <Calendar size={14} />
                        Agregar Nuevo Mes
                      </h5>

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: isMobile
                            ? "1fr"
                            : "1fr 1fr auto",
                          gap: "8px",
                          alignItems: "end",
                        }}
                      >
                        <div>
                          <label
                            style={{
                              display: "block",
                              marginBottom: "4px",
                              fontSize: "11px",
                              fontWeight: "600",
                              color: "#374151",
                            }}
                          >
                            Mes/Año
                          </label>
                          <input
                            type="month"
                            value={nuevaFecha}
                            onChange={(e) => setNuevaFecha(e.target.value)}
                            style={{
                              width: "100%",
                              padding: "8px",
                              border: "1px solid #d1d5db",
                              borderRadius: "6px",
                              fontSize: "12px",
                              boxSizing: "border-box",
                            }}
                          />
                        </div>

                        <div>
                          <label
                            style={{
                              display: "block",
                              marginBottom: "4px",
                              fontSize: "11px",
                              fontWeight: "600",
                              color: "#374151",
                            }}
                          >
                            Valor del Índice
                          </label>
                          <input
                            type="number"
                            step="0.0001"
                            value={nuevoValor}
                            onChange={(e) => setNuevoValor(e.target.value)}
                            placeholder="1450.5000"
                            style={{
                              width: "100%",
                              padding: "8px",
                              border: "1px solid #d1d5db",
                              borderRadius: "6px",
                              fontSize: "12px",
                              boxSizing: "border-box",
                            }}
                          />
                        </div>

                        <div style={{ display: "flex", gap: "4px" }}>
                          <button
                            onClick={() => {
                              if (!nuevaFecha || !nuevoValor) {
                                showCustomAlert(
                                  "error",
                                  "Error",
                                  "Por favor completa mes/año y valor"
                                );
                                return;
                              }
                              const fechaCompleta = nuevaFecha + "-01";
                              guardarValorPersonalizado(
                                fechaCompleta,
                                nuevoValor
                              );
                            }}
                            style={{
                              background: "#059669",
                              color: "white",
                              border: "none",
                              padding: "8px 12px",
                              borderRadius: "6px",
                              fontSize: "11px",
                              cursor: "pointer",
                              fontWeight: "600",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <CheckCircle2 size={12} />
                            Guardar
                          </button>
                          <button
                            onClick={() => {
                              setEditandoMes(null);
                              setNuevoValor("");
                              setNuevaFecha("");
                            }}
                            style={{
                              background: "#dc2626",
                              color: "white",
                              border: "none",
                              padding: "8px 12px",
                              borderRadius: "6px",
                              fontSize: "11px",
                              cursor: "pointer",
                              fontWeight: "600",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <X size={12} />
                            Cancelar
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* SI ES CAC OFICIAL: Tabla tradicional con override */
                <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "12px",
                    }}
                  >
                    <thead
                      style={{
                        position: "sticky",
                        top: 0,
                        background: "#f8fafc",
                      }}
                    >
                      <tr>
                        <th
                          style={{
                            padding: "8px",
                            textAlign: "left",
                            fontWeight: "600",
                            color: "#475569",
                            fontSize: "11px",
                          }}
                        >
                          Mes
                        </th>
                        <th
                          style={{
                            padding: "8px",
                            textAlign: "center",
                            fontWeight: "600",
                            color: "#475569",
                            fontSize: "11px",
                          }}
                        >
                          Valor Oficial CAC
                        </th>
                        <th
                          style={{
                            padding: "8px",
                            textAlign: "center",
                            fontWeight: "600",
                            color: "#475569",
                            fontSize: "11px",
                          }}
                        >
                          Valor Personalizado
                        </th>
                        <th
                          style={{
                            padding: "8px",
                            textAlign: "center",
                            fontWeight: "600",
                            color: "#475569",
                            fontSize: "11px",
                          }}
                        >
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {generarMesesParaConfigurar().map((mes, index) => (
                        <tr
                          key={mes.fecha}
                          style={{
                            borderBottom: "1px solid #f1f5f9",
                            background: index % 2 === 0 ? "white" : "#f8fafc",
                          }}
                        >
                          <td
                            style={{
                              padding: "8px",
                              fontWeight: "600",
                              textTransform: "capitalize",
                              fontSize: "11px",
                            }}
                          >
                            {mes.mes}
                          </td>
                          <td style={{ padding: "8px", textAlign: "center" }}>
                            {mes.valorOficial ? (
                              <span
                                style={{ color: "#64748b", fontSize: "11px" }}
                              >
                                {mes.valorOficial.toFixed(2)}
                              </span>
                            ) : (
                              <span
                                style={{ color: "#dc2626", fontSize: "11px" }}
                              >
                                No disponible
                              </span>
                            )}
                          </td>
                          <td style={{ padding: "8px", textAlign: "center" }}>
                            {editandoMes === mes.fecha ? (
                              <div
                                style={{
                                  display: "flex",
                                  gap: "4px",
                                  justifyContent: "center",
                                }}
                              >
                                <input
                                  type="number"
                                  value={nuevoValor}
                                  onChange={(e) =>
                                    setNuevoValor(e.target.value)
                                  }
                                  placeholder={
                                    mes.valorOficial?.toFixed(2) || "0.00"
                                  }
                                  style={{
                                    width: "70px",
                                    padding: "3px 5px",
                                    border: "1px solid #d1d5db",
                                    borderRadius: "4px",
                                    fontSize: "11px",
                                  }}
                                />
                                <button
                                  onClick={() =>
                                    guardarValorPersonalizado(
                                      mes.fecha,
                                      nuevoValor
                                    )
                                  }
                                  style={{
                                    background: "#059669",
                                    color: "white",
                                    border: "none",
                                    padding: "3px 6px",
                                    borderRadius: "4px",
                                    fontSize: "10px",
                                    cursor: "pointer",
                                  }}
                                >
                                  <CheckCircle2 size={8} />
                                </button>
                                <button
                                  onClick={() => {
                                    setEditandoMes(null);
                                    setNuevoValor("");
                                  }}
                                  style={{
                                    background: "#dc2626",
                                    color: "white",
                                    border: "none",
                                    padding: "3px 6px",
                                    borderRadius: "4px",
                                    fontSize: "10px",
                                    cursor: "pointer",
                                  }}
                                >
                                  <X size={8} />
                                </button>
                              </div>
                            ) : mes.tienePersonalizado ? (
                              <span
                                style={{
                                  color: colores.primary,
                                  fontWeight: "600",
                                  fontSize: "11px",
                                }}
                              >
                                {mes.valorPersonalizado.toFixed(2)}
                              </span>
                            ) : (
                              <span
                                style={{ color: "#9ca3af", fontSize: "11px" }}
                              >
                                -
                              </span>
                            )}
                          </td>
                          <td style={{ padding: "8px", textAlign: "center" }}>
                            <div
                              style={{
                                display: "flex",
                                gap: "4px",
                                justifyContent: "center",
                              }}
                            >
                              {editandoMes === mes.fecha ? null : (
                                <>
                                  <button
                                    onClick={() => {
                                      setEditandoMes(mes.fecha);
                                      setNuevoValor(
                                        mes.valorPersonalizado?.toString() ||
                                          mes.valorOficial?.toString() ||
                                          ""
                                      );
                                    }}
                                    style={{
                                      background: colores.primary,
                                      color: "white",
                                      border: "none",
                                      padding: "3px 6px",
                                      borderRadius: "4px",
                                      fontSize: "9px",
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "2px",
                                    }}
                                  >
                                    <Edit size={6} />
                                    {mes.tienePersonalizado
                                      ? "Editar"
                                      : "Personalizar"}
                                  </button>
                                  {mes.tienePersonalizado && (
                                    <button
                                      onClick={() =>
                                        eliminarValorPersonalizado(mes.fecha)
                                      }
                                      style={{
                                        background: "#dc2626",
                                        color: "white",
                                        border: "none",
                                        padding: "3px 6px",
                                        borderRadius: "4px",
                                        fontSize: "9px",
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "2px",
                                      }}
                                    >
                                      <Trash2 size={6} />
                                      Eliminar
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Explicación del sistema */}
      <div
        style={{
          background: "#eff6ff",
          border: "1px solid #bfdbfe",
          borderRadius: "12px",
          padding: "16px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "12px",
          }}
        >
          <Info size={20} color="#1e40af" />
          <div>
            <h5
              style={{
                margin: "0 0 6px 0",
                fontSize: "13px",
                fontWeight: "600",
                color: "#1e40af",
              }}
            >
              ¿Cómo funciona el sistema de actualización?
            </h5>
            {datos.seActualiza ? (
              <ul
                style={{
                  margin: 0,
                  paddingLeft: "16px",
                  fontSize: "12px",
                  color: "#1e40af",
                  lineHeight: "1.5",
                }}
              >
                <li>
                  <strong>Presupuesto inicial:</strong> Se convierte a
                  "unidades" usando el índice del mes de inicio del proyecto
                </li>
                <li>
                  <strong>Pagos mensuales:</strong> Se convierten a unidades
                  usando el índice oficial del mes (o provisorio si no está
                  disponible)
                </li>
                <li>
                  <strong>Índice provisorio:</strong> Si no hay índice del mes,
                  se usa el último disponible hasta que se publique el oficial
                </li>
                <li>
                  <strong>Saldo actual:</strong> Se calcula multiplicando las
                  unidades restantes por el último índice disponible
                </li>
                <li>
                  <strong>Valores personalizados:</strong> Puedes modificar
                  cualquier mes específico si negociaste condiciones especiales
                </li>
                {esMetodoPersonalizado(datos.metodoActualizacion) && (
                  <li>
                    <strong>Sistema personalizado:</strong> Todos los índices
                    deben ser configurados manualmente mes a mes
                  </li>
                )}
              </ul>
            ) : (
              <ul
                style={{
                  margin: 0,
                  paddingLeft: "16px",
                  fontSize: "12px",
                  color: "#1e40af",
                  lineHeight: "1.5",
                }}
              >
                <li>
                  <strong>Valor fijo:</strong> Este concepto mantiene su valor
                  original sin actualizaciones por inflación
                </li>
                <li>
                  <strong>Cálculo simple:</strong> Saldo = Presupuesto inicial -
                  Total pagado
                </li>
                <li>
                  <strong>Sin índices:</strong> No se aplican sistemas de
                  actualización, ideal para conceptos que no se ajustan
                </li>
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Información adicional sobre publicación */}
      {datos.seActualiza &&
        !esMetodoPersonalizado(datos.metodoActualizacion) && (
          <div
            style={{
              background: "#fefce8",
              border: "1px solid #facc15",
              borderRadius: "12px",
              padding: "16px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "12px",
              }}
            >
              <AlertCircle size={20} color="#92400e" />
              <div>
                <h5
                  style={{
                    margin: "0 0 6px 0",
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#92400e",
                  }}
                >
                  Publicación del CAC
                </h5>
                <p
                  style={{
                    margin: 0,
                    fontSize: "12px",
                    color: "#92400e",
                    lineHeight: "1.5",
                  }}
                >
                  El CAC se publica generalmente el día 20 de cada mes con los
                  valores del mes anterior. Por ejemplo, el 20 de junio se
                  publica el CAC de mayo. Hasta que se publique el oficial, se
                  usa el último disponible como provisorio. Los valores
                  personalizados siempre tienen prioridad.
                </p>
              </div>
            </div>
          </div>
        )}

      {/* Información sobre sistema personalizado */}
      {datos.seActualiza &&
        esMetodoPersonalizado(datos.metodoActualizacion) && (
          <div
            style={{
              background: "#f0fdf4",
              border: "1px solid #22c55e",
              borderRadius: "12px",
              padding: "16px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "12px",
              }}
            >
              <Settings size={20} color="#166534" />
              <div>
                <h5
                  style={{
                    margin: "0 0 6px 0",
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#166534",
                  }}
                >
                  Sistema Personalizado: {datos.metodoActualizacion}
                </h5>
                <p
                  style={{
                    margin: 0,
                    fontSize: "12px",
                    color: "#166534",
                    lineHeight: "1.5",
                  }}
                >
                  Este proyecto usa un sistema de actualización completamente
                  personalizado. Todos los valores deben ser configurados
                  manualmente mes a mes usando la tabla superior. Los meses sin
                  configurar no podrán ser calculados correctamente.
                </p>
              </div>
            </div>
          </div>
        )}
    </div>
  );
}

export default HonorariosMO;
